import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { FaCrown, FaEye, FaComment, FaClock, FaCheck, FaRupeeSign, FaCreditCard, FaPaypal } from 'react-icons/fa';
import Select from "react-select";
import Cropper from "react-easy-crop";
import { State, City } from "country-state-city";
import { SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Plus, ArrowLeft, Trash2, Upload, Eye, EyeOff, Crop as CropIcon, Square } from 'lucide-react';
import "../../styles/usemagementcreate.css";
import { postWithTokenAPI, getWithTokenAPI, deleteWithTokenAPI, putWithTokenAPI, BASE_URL, getWithoutTokenAPI } from '../../util/Api';
import Swal from 'sweetalert2';
import Sidebar from './Sidebar';
import Topbar from './Topbar';
import { useTranslation } from 'react-i18next';
import { User, Camera, Edit, CheckCircle } from "lucide-react";
import axios from 'axios';

// Move DeleteConfirmationModal outside the main component to fix hook error
const DeleteConfirmationModal = ({ isOpen, onClose, onConfirm, userName, mobileNumber, isLoading }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-md w-full mx-auto transform transition-all">
        {/* Modal Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h3 className="text-xl font-semibold text-gray-900">Delete User Profile</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            disabled={isLoading}
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Modal Body */}
        <div className="p-6">
          <div className="text-center mb-6">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            </div>
            <h4 className="text-lg font-semibold text-gray-900 mb-2">
              Are you sure you want to delete this user profile?
            </h4>
            <p className="text-gray-600 text-sm mb-4">
              This action cannot be undone. All user data will be permanently removed from the system.
            </p>

            {userName && (
              <div className="bg-gray-50 rounded-lg p-4 border-l-4 border-red-500">
                <p className="text-sm font-medium text-gray-900 mb-1">User to be deleted:</p>
                <p className="text-lg font-semibold text-gray-900">{userName}</p>
                {mobileNumber && (
                  <p className="text-sm text-gray-600 mt-1">Mobile: {mobileNumber}</p>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Modal Footer */}
        <div className="flex gap-3 p-6 border-t border-gray-200 bg-gray-50 rounded-b-xl">
          <button
            onClick={onClose}
            disabled={isLoading}
            className="flex-1 px-4 py-3 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium"
          >
            No, Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={isLoading}
            className="flex-1 px-4 py-3 text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium flex items-center justify-center gap-2"
          >
            {isLoading ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Deleting...
              </>
            ) : (
              'Yes, Delete'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

const UserManagementCreate = () => {
  const navigate = useNavigate();
  const { id, tab } = useParams();
  const location = useLocation();
  const [activeSection, setActiveSection] = useState('users');
  const [activeTab, setActiveTab] = useState('mobile-info');

  const isEditMode = Boolean(id);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const { t, i18n } = useTranslation();
  const lang = i18n.language === "ta" ? "ta" : "en";


  const educationOptionsList = {
    en: [
      { label: "No formal education", value: "No formal education" },
      { label: "Primary school", value: "Primary school" },
      { label: "secondary school", value: "secondary school" },
      { label: "Higher secondary", value: "Higher secondary" },
      { label: "Diploma", value: "Diploma" },
      { label: "Vocational training", value: "Vocational training" },
      { label: "Bachelor’s degree", value: "Bachelor’s degree" },
      { label: "Master’s degree", value: "Master’s degree" },
      { label: "M.Phil", value: "M.Phil" },
      { label: "Doctorate / Ph.D", value: "Doctorate / Ph.D" },
      { label: "Professional degree", value: "Professional degree" },
      { label: "Medical degree ", value: "Medical degree" },
      { label: "Engineering degree ", value: "Engineering degree" },
      { label: "Other", value: "Other" }
    ],

    ta: [
      { label: "முறையான கல்வி இல்லை", value: "No formal education" },
      { label: "தொடக்கப் பள்ளி", value: "Primary school" },
      { label: "உயர்நிலைப் பள்ளி", value: "secondary school" },
      { label: "மேல்நிலை பள்ளி", value: "Higher secondary" },
      { label: "டிப்ளமோ", value: "Diploma" },
      { label: "தொழிற்கல்வி பயிற்சி", value: "Vocational training" },
      { label: "இளங்கலை பட்டம்", value: "Bachelor’s degree" },
      { label: "முதுக்கலை பட்டம்", value: "Master’s degree" },
      { label: "எம்.ஃபில்", value: "M.Phil" },
      { label: "டாக்டர் / பிஎச்.டி", value: "Doctorate / Ph.D" },
      { label: "தொழில்முறை பட்டம் ", value: "Professional degree" },
      { label: "மருத்துவ பட்டம்", value: "Medical degree" },
      { label: "பொறியியல் பட்டம்", value: "Engineering degree" },
      { label: "மற்றவை", value: "Other" }
    ]
  };
  const educationOptions = educationOptionsList[lang];

  const religionLis = {
    en: [
      { label: "Hindu", value: "Hindu" },
      { label: "Christian", value: "Christian" },
      { label: "Muslim", value: "Muslim" },
      { label: "Buddhist", value: "Buddhist" },
      { label: "Jain", value: "Jain" },
      { label: "Sikh", value: "Sikh" },
      { label: "Prefer not to say", value: "No Caste Barrier" }
    ],
    ta: [
      { label: "இந்து", value: "Hindu" },
      { label: "கிறிஸ்துவர்", value: "Christian" },
      { label: "முஸ்லிம்", value: "Muslim" },
      { label: "பௌத்தர்", value: "Buddhist" },
      { label: "சமணம்", value: "Jain" },
      { label: "சீக்கியம்", value: "Sikh" },
      { label: "கூற விருப்பம் இல்லை", value: "No Caste Barrier" }
    ]
  };

  const religionLisProfile = {
    en: [
      { label: "Hindu", value: "Hindu" },
      { label: "Christian", value: "Christian" },
      { label: "Muslim", value: "Muslim" },
      { label: "Buddhist", value: "Buddhist" },
      { label: "Jain", value: "Jain" },
      { label: "Sikh", value: "Sikh" },
      { label: "Prefer not to say", value: "No Caste Barrier" }
    ],
    ta: [
      { label: "இந்து", value: "Hindu" },
      { label: "கிறிஸ்துவர்", value: "Christian" },
      { label: "முஸ்லிம்", value: "Muslim" },
      { label: "பௌத்தர்", value: "Buddhist" },
      { label: "சமணம்", value: "Jain" },
      { label: "சீக்கியம்", value: "Sikh" },
      { label: "மதம் தடை இல்லை", value: "No Caste Barrier" }
    ]
  };


  const religionList = religionLis[lang];
  const religionListPro = religionLisProfile[lang];

  const dietTypesList = {
    en: [
      { label: "Vegetarian", value: "Vegetarian" },
      { label: "Non-Vegetarian", value: "Non-Vegetarian" },
      { label: "Eggetarian", value: "Eggetarian" }
    ],
    ta: [
      { label: "சைவம்", value: "Vegetarian" },
      { label: "அசைவம்", value: "Non-Vegetarian" },
      { label: "முட்டை உண்பவர்", value: "Eggetarian" }
    ]
  };

  const doshamTypesList = {
    en: [
      { label: "No", value: "No" },
      { label: "Yes", value: "Yes" },
      { label: "Don't Know", value: "Don't Know" }
    ],
    ta: [
      { label: "இல்லை", value: "No" },
      { label: "ஆம்", value: "Yes" },
      { label: "தெரியவில்லை", value: "Don't Know" }
    ]
  };

  const kujaDoshamTypesList = {
    en: [
      { label: "No", value: "No" },
      { label: "Yes", value: "Yes" },
      { label: "Don't Know", value: "Don't Know" }
    ],
    ta: [
      { label: "இல்லை", value: "No" },
      { label: "ஆம்", value: "Yes" },
      { label: "தெரியவில்லை", value: "Don't Know" }
    ]
  };
  const familyStatusList = {
    en: [
      { label: "Middle Class", value: "Middle Class" },
      { label: "Upper Middle Class", value: "Upper Middle Class" },
      { label: "Rich", value: "Rich" }
    ],
    ta: [
      { label: "நடுத்தரம் ", value: "Middle Class" },
      { label: "மேல் நடுத்தரம்", value: "Upper Middle Class" },
      { label: "செல்வந்தர்", value: "Rich" }
    ]
  };

  const familyValuesOptionsList = {
    en: [
      { label: "Traditional", value: "Traditional" },
      { label: "Moderate", value: "Moderate" },
      { label: "Liberal", value: "Liberal" }
    ],
    ta: [
      { label: "சாதாரணம்", value: "Traditional" },
      { label: "மிதமானது", value: "Moderate" },
      { label: "மீறியது", value: "Liberal" }
    ]
  };

  const familyTypeOptionsList = {
    en: [
      { label: "Joint", value: "Joint" },
      { label: "Nuclear", value: "Nuclear" }
    ],
    ta: [
      { label: "கூட்டு குடும்பம்", value: "Joint" },
      { label: "பிளவு குடும்பம்", value: "Nuclear" }
    ]
  };

  const countryOptions = [
    { label: "+91 India", value: "+91" },
    { label: "+1 USA", value: "+1" },
    { label: "+44 UK", value: "+44" },
    { label: "+81 Japan", value: "+81" },
    // add more countries as needed
  ];


  // Select options dynamically based on lang
  const familyValuesOptions = familyValuesOptionsList[lang];
  const familyTypeOptions = familyTypeOptionsList[lang];

  const dietTypes = dietTypesList[lang];
  const doshamTypes = doshamTypesList[lang];
  const kujaDoshamTypes = kujaDoshamTypesList[lang];
  const familyStatusOptions = familyStatusList[lang];

  const allowedZodiacValues = [
    'pu', 'cha', 'chk', 'sun', 'viy', 'sev', 'kee', 'lac', 'ragu', 'sat'
  ];

  // Form states
  const [userManagementFormData, setUserManagementFormData] = useState({
    email: "",
    password: "",
    mobile_number: "",
    country_code: "+91",
    name: "",
    gender: "",
    dob: "",
    age: "",
    city: "",
    state: "",
    district: "",
    maritalStatus: "",
    profileImage: "",
    status: "Active",
    education: "",
    profession: "",
    income: "",
    height: "",
    weight: "",
    motherTongue: "",
    religion: "",
    caste: "",
    subCaste: "",
    diet: "",
    gallery: [],
    rasi: "",
    natchathiram: "",
    gothram: "",
    laknam: "",
    dosham: "",
    kujaDosham: "",
    gender_preference: "",
    min_age: "",
    max_age: "",
    marital_status: [],
    mother_tongue: [],
    religion_pref: [],
    caste_pref: [],
    sub_caste: [],
    education_level: [],
    city_pref: [],
    father_name: '',
    family_status: [],
    family_type: [],
    family_values: [],
    about_partner: "",
    height_range: { min: "", max: "" },
    zodiac: {
      rasi: {
        House_1: [], House_2: [], House_3: [], House_4: [], House_5: [], House_6: [],
        House_7: [], House_8: [], House_9: [], House_10: [], House_11: [], House_12: []
      },
      navamsa: {
        House_1: [], House_2: [], House_3: [], House_4: [], House_5: [], House_6: [],
        House_7: [], House_8: [], House_9: [], House_10: [], House_11: [], House_12: []
      }
    },
    isVerified: false
  });

  const [statesList, setStatesList] = useState([]);
  const [districtList, setDistrictList] = useState([]);
  const [selectedState, setSelectedState] = useState("");


  console.log(userManagementFormData, 'userManagementFormData')
  useEffect(() => {
    const states = State.getStatesOfCountry("IN"); // get all Indian states
    setStatesList(states); // store whole object
  }, []);

  const statesOptions = statesList.map((s) => ({
    value: s.name,
    label: s.name,
    isoCode: s.isoCode, // important!
  }));

  const districtsOptions = districtList.map((d) => ({
    value: d,
    label: d,
  }));


  const handleStateChange = (selectedOption, name) => {
    if (!selectedOption) {
      setSelectedState("");
      setDistrictList([]);
      handleFieldChange(name, "");
      return;
    }

    setSelectedState(selectedOption.value);
    handleFieldChange(name, selectedOption.value);

    const state = statesList.find(
      (item) => item.name === selectedOption.value
    );

    if (state) {
      const cities = City.getCitiesOfState("IN", state.isoCode);
      setDistrictList(cities.map((c) => c.name));
    }
  };



  const [userManagementCreateLoading, setUserManagementCreateLoading] = useState(false);
  const [loadingUserData, setLoadingUserData] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [originalMobileNumber, setOriginalMobileNumber] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});
  console.log(fieldErrors, "fieldErrors")
  const [zodiacErrors, setZodiacErrors] = useState({});
  const [uploadingImage, setUploadingImage] = useState(false);
  const [blockLoading, setBlockLoading] = useState(false);

  const [subscriptions, setsubscriptions] = useState(false)
  const [subscriptionsUserId, setSubscriptionsUserId] = useState(null)

  const tabs = [
    { id: 'mobile-info', label: t("userForm.userTabs.mobileInfo"), path: 'mobile-info' },
    { id: 'basic-info', label: t("userForm.userTabs.basicInfo"), path: 'basic-info' },
    // { id: 'account-status', label: t("userForm.userTabs.accountStatus"), path: 'account-status' },
    { id: 'family-details', label: t("userForm.userTabs.familyDetails"), path: 'family-details' },
    { id: 'professional-info', label: t("userForm.userTabs.professionalInfo"), path: 'professional-info' },
    { id: 'astrology-info', label: t("userForm.userTabs.astrologyInfo"), path: 'astrology-info' },
    { id: 'partner-preferences', label: t("userForm.userTabs.partnerPreferences"), path: 'partner-preferences' },
    { id: 'zodiac-details', label: t("userForm.userTabs.zodiacDetails"), path: 'zodiac-details' },
    ...(subscriptions && subscriptionsUserId
      ? [{ id: 'subscriptions', label: "Subscriptions" }]
      : []),
    ...(isEditMode
      ? [{ id: 'subscriptions', label: "Subscriptions" }]
      : [])
  ];

  const casteOptions = [
    { value: "nadar", label: t("userForm.BasicuserForm.fields.caste.options.nadar") },
    { value: "pillai", label: t("userForm.BasicuserForm.fields.caste.options.pillai") },
    { value: "naidu", label: t("userForm.BasicuserForm.fields.caste.options.naidu") },
    { value: "ilathu_pillai", label: t("userForm.BasicuserForm.fields.caste.options.ilathu_pillai") },
    { value: "vishwakarma", label: t("userForm.BasicuserForm.fields.caste.options.vishwakarma") },
    { value: "saiva_pillai", label: t("userForm.BasicuserForm.fields.caste.options.saiva_pillai") },
    { value: "kallar", label: t("userForm.BasicuserForm.fields.caste.options.kallar") },
    { value: "maravar", label: t("userForm.BasicuserForm.fields.caste.options.maravar") },
    { value: "agamudaiyar", label: t("userForm.BasicuserForm.fields.caste.options.agamudaiyar") },
    { value: "chettiyar", label: t("userForm.BasicuserForm.fields.caste.options.chettiyar") },
    { value: "christian", label: t("userForm.BasicuserForm.fields.caste.options.christian") },
    { value: "muslim", label: t("userForm.BasicuserForm.fields.caste.options.muslim") },
    { value: "maruthuvar", label: t("userForm.BasicuserForm.fields.caste.options.maruthuvar") },
    { value: "sourashtra", label: t("userForm.BasicuserForm.fields.caste.options.sourashtra") },
    { value: "vanniyar", label: t("userForm.BasicuserForm.fields.caste.options.vanniyar") },
    { value: "naicker", label: t("userForm.BasicuserForm.fields.caste.options.naicker") },
    { value: "muppanar", label: t("userForm.BasicuserForm.fields.caste.options.muppanar") },
    { value: "veerasaivam", label: t("userForm.BasicuserForm.fields.caste.options.veerasaivam") },
    { value: "malayali", label: t("userForm.BasicuserForm.fields.caste.options.malayali") },
    { value: "kulalar", label: t("userForm.BasicuserForm.fields.caste.options.kulalar") },
    { value: "iyer", label: t("userForm.BasicuserForm.fields.caste.options.iyer") },
    { value: "iyengar", label: t("userForm.BasicuserForm.fields.caste.options.iyengar") },
    { value: "widow_remarriage", label: t("userForm.BasicuserForm.fields.caste.options.widow_remarriage") },
    { value: "mudaliyar", label: t("userForm.BasicuserForm.fields.caste.options.mudaliyar") },
    { value: "yadavar", label: t("userForm.BasicuserForm.fields.caste.options.yadavar") },
    { value: "reddy", label: t("userForm.BasicuserForm.fields.caste.options.reddy") },
    { value: "vannar", label: t("userForm.BasicuserForm.fields.caste.options.vannar") },
    { value: "doctor", label: t("userForm.BasicuserForm.fields.caste.options.doctor") },
    { value: "adi_dravidar", label: t("userForm.BasicuserForm.fields.caste.options.adi_dravidar") },
    { value: "devendra_kula_vellalar", label: t("userForm.BasicuserForm.fields.caste.options.devendra_kula_vellalar") },
    { value: "arunthathiyar", label: t("userForm.BasicuserForm.fields.caste.options.arunthathiyar") },
    { value: "kolandar", label: t("userForm.BasicuserForm.fields.caste.options.kolandar") },
    { value: "No Caste Barrier", label: t("userForm.BasicuserForm.fields.caste.options.no_caste_barrier") },
  ];

  const casteOptionspreferences = [
    { value: "nadar", label: t("userForm.BasicuserForm.fields.caste.options.nadar") },
    { value: "pillai", label: t("userForm.BasicuserForm.fields.caste.options.pillai") },
    { value: "naidu", label: t("userForm.BasicuserForm.fields.caste.options.naidu") },
    { value: "ilathu_pillai", label: t("userForm.BasicuserForm.fields.caste.options.ilathu_pillai") },
    { value: "vishwakarma", label: t("userForm.BasicuserForm.fields.caste.options.vishwakarma") },
    { value: "saiva_pillai", label: t("userForm.BasicuserForm.fields.caste.options.saiva_pillai") },
    { value: "kallar", label: t("userForm.BasicuserForm.fields.caste.options.kallar") },
    { value: "maravar", label: t("userForm.BasicuserForm.fields.caste.options.maravar") },
    { value: "agamudaiyar", label: t("userForm.BasicuserForm.fields.caste.options.agamudaiyar") },
    { value: "chettiyar", label: t("userForm.BasicuserForm.fields.caste.options.chettiyar") },
    { value: "christian", label: t("userForm.BasicuserForm.fields.caste.options.christian") },
    { value: "muslim", label: t("userForm.BasicuserForm.fields.caste.options.muslim") },
    { value: "maruthuvar", label: t("userForm.BasicuserForm.fields.caste.options.maruthuvar") },
    { value: "sourashtra", label: t("userForm.BasicuserForm.fields.caste.options.sourashtra") },
    { value: "vanniyar", label: t("userForm.BasicuserForm.fields.caste.options.vanniyar") },
    { value: "naicker", label: t("userForm.BasicuserForm.fields.caste.options.naicker") },
    { value: "muppanar", label: t("userForm.BasicuserForm.fields.caste.options.muppanar") },
    { value: "veerasaivam", label: t("userForm.BasicuserForm.fields.caste.options.veerasaivam") },
    { value: "malayali", label: t("userForm.BasicuserForm.fields.caste.options.malayali") },
    { value: "kulalar", label: t("userForm.BasicuserForm.fields.caste.options.kulalar") },
    { value: "iyer", label: t("userForm.BasicuserForm.fields.caste.options.iyer") },
    { value: "iyengar", label: t("userForm.BasicuserForm.fields.caste.options.iyengar") },
    { value: "widow_remarriage", label: t("userForm.BasicuserForm.fields.caste.options.widow_remarriage") },
    { value: "mudaliyar", label: t("userForm.BasicuserForm.fields.caste.options.mudaliyar") },
    { value: "yadavar", label: t("userForm.BasicuserForm.fields.caste.options.yadavar") },
    { value: "reddy", label: t("userForm.BasicuserForm.fields.caste.options.reddy") },
    { value: "vannar", label: t("userForm.BasicuserForm.fields.caste.options.vannar") },
    { value: "doctor", label: t("userForm.BasicuserForm.fields.caste.options.doctor") },
    { value: "adi_dravidar", label: t("userForm.BasicuserForm.fields.caste.options.adi_dravidar") },
    { value: "devendra_kula_vellalar", label: t("userForm.BasicuserForm.fields.caste.options.devendra_kula_vellalar") },
    { value: "arunthathiyar", label: t("userForm.BasicuserForm.fields.caste.options.arunthathiyar") },
    { value: "kolandar", label: t("userForm.BasicuserForm.fields.caste.options.kolandar") },
    { value: "No Caste Barrier", label: t("userForm.BasicuserForm.fields.caste.options.no_caste_barrier1") },
  ];



  // UPDATED Validation rules
  const validateField = (name, value) => {
    const errors = {};

    switch (name) {
      case 'name':
        if (!value?.trim()) errors.name = 'Name is required';
        break;
      case 'gender':
        if (!value?.trim()) errors.gender = 'Gender is required';
        break;
      case 'age':
        if (!value) errors.age = 'Age is required';
        else if (value < 18) errors.age = 'Valid age (18+) is required';
        break;
      case 'mobile_number':
        if (!value?.trim()) errors.mobile_number = 'Mobile number is required';
        else if (value.length !== 10) errors.mobile_number = 'Mobile number must be exactly 10 digits';
        break;
      case 'country_code':
        if (!value?.trim()) errors.country_code = 'Country code is required';
        break;
      case 'status':
        if (!value?.trim()) errors.status = 'Status is required';
        break;
      case 'dob':
        if (!value?.trim()) errors.dob = 'Date of birth is required';
        break;
      case 'city':
        if (!value?.trim()) errors.city = 'City is required';
        break;
      case 'district':
        if (!value?.trim()) errors.district = 'District is required';
        break;
      case 'maritalStatus':
        if (!value?.trim()) errors.maritalStatus = 'Marital status is required';
        break;
      case 'education':
        if (!value?.trim()) errors.education = 'Education is required';
        break;
      case 'profession':
        if (!value?.trim()) errors.profession = 'Profession is required';
        break;
      case 'height':
        if (value && value < 100) errors.height = 'Height must be at least 100 cm';
        break;
      case 'weight':
        if (value && value < 30) errors.weight = 'Weight must be at least 30 kg';
        break;
      case 'min_age':
        if (!value) errors.min_age = 'Minimum age is required';
        else if (value < 18) errors.min_age = 'Minimum age must be at least 18';
        break;
      case 'max_age':
        if (!value) errors.max_age = 'Maximum age is required';
        else if (value < 18) errors.max_age = 'Maximum age must be at least 18';
        break;
      default:
        break;
    }

    return errors;
  };


  const handleZodiacUpdate = (section, house, value) => {
    const displayValue = value;

    let processedArray = value
      .split(/\s+/) // Split by one or more spaces
      .map(item => item.trim())
      .filter(item => item !== '')
      .slice(0, 3); // Limit to maximum 3 items

    setUserManagementFormData(prev => ({
      ...prev,
      zodiac: {
        ...prev.zodiac,
        [section]: {
          ...prev.zodiac[section],
          [house]: processedArray
        }
      }
    }));

    setZodiacErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors[`${section}_${house}`];
      return newErrors;
    });
  };

  const handleFieldChange = (field, value) => {
    console.log(field, value, "field, value===")
    setUserManagementFormData(prev => ({ ...prev, [field]: value }));

    const errors = validateField(field, value);

    if (Object.keys(errors).length === 0) {
      setFieldErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    } else {
      setFieldErrors(prev => ({
        ...prev,
        ...errors
      }));
    }
  };

  // Toast notification function
  const showToast = (message, type = 'success') => {
    const Toast = Swal.mixin({
      toast: true,
      position: 'top-end',
      showConfirmButton: false,
      timer: 3000,
      timerProgressBar: true,
      background: type === 'success' ? '#10b981' : '#ef4444',
      color: 'white',
      didOpen: (toast) => {
        toast.onmouseenter = Swal.stopTimer;
        toast.onmouseleave = Swal.resumeTimer;
      }
    });

    Toast.fire({
      icon: type,
      title: message
    });
  };

  // Function to calculate age from DOB
  const calculateAgeFromDOB = (dobString) => {
    if (!dobString) return '';

    try {
      const dob = new Date(dobString);
      if (isNaN(dob.getTime())) {
        console.warn('Invalid date:', dobString);
        return '';
      }

      const today = new Date();
      let age = today.getFullYear() - dob.getFullYear();
      const monthDiff = today.getMonth() - dob.getMonth();

      if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dob.getDate())) {
        age--;
      }

      return age.toString();
    } catch (error) {
      console.error('Age calculation error:', error);
      return '';
    }
  };


  // Load user data when in edit mode
  useEffect(() => {
    if (isEditMode && id) {
      console.log("Edit mode activated for user ID:", id);
      loadUserData();
    }
  }, [isEditMode, id]);

  const handleDOBChange = (dob) => {
    // Convert from yyyy-MM-dd (HTML input) to dd-MM-yyyy (backend format)
    const formattedDOB = formatDateToDDMMYYYY(dob);
    const calculatedAge = calculateAgeFromDOB(dob);

    setUserManagementFormData(prev => ({
      ...prev,
      dob: dob, // Store in yyyy-MM-dd format for the input
      age: calculatedAge
    }));

    const dobErrors = validateField('dob', dob);
    const ageErrors = validateField('age', calculatedAge);

    setFieldErrors(prev => {
      const newErrors = { ...prev };
      if (!dobErrors.dob) delete newErrors.dob;
      if (!ageErrors.age) delete newErrors.age;
      return {
        ...newErrors,
        ...dobErrors,
        ...ageErrors
      };
    });
  };

  // FIXED: Update the setup function to handle date format conversion
  const setupUserManagementEditForm = (userData) => {
    if (!userData) return;

    console.log("Setting up form with user data:", userData);

    // Convert backend dd-MM-yyyy format to yyyy-MM-dd for HTML input
    const backendDOB = userData.basicProfile?.dob || "";
    let frontendDOB = "";

    if (backendDOB) {
      try {
        const [day, month, year] = backendDOB.split('-');
        if (day && month && year) {
          frontendDOB = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
        }
      } catch (error) {
        console.warn('Date conversion error:', error);
      }
    }

    const formData = {
      email: userData.email || userData.basicProfile?.email || "",
      mobile_number: userData.basicProfile?.mobile_number || "",
      country_code: userData.country_code || "+91",
      name: userData.basicProfile?.name || "",
      gender: userData.basicProfile?.gender || "",
      dob: frontendDOB, // Use converted date format
      // ... rest of your form data
      state: userData.basicProfile?.state || "",
      district: userData.basicProfile?.district || "",
      age: userData.basicProfile?.age || "",
      maritalStatus: userData.basicProfile?.maritalStatus || "",
      profileImage: userData.basicProfile?.profileImage || "",
      child_count: userData.basicProfile?.child_count || "",
      status: userData.status || "Active",
      education: userData.professionalProfile?.education || "",
      profession: userData.professionalProfile?.profession || "",
      income: userData.professionalProfile?.income || "",
      height: userData.professionalProfile?.height || "",
      documentId: userData.professionalProfile.documentId,
      father_name: userData.professionalProfile?.father_name || "",
      mother_name: userData.professionalProfile?.mother_name || "",
      siblings: userData.professionalProfile?.siblings || "",
      property_details: userData.professionalProfile?.property_details || "",
      instagram: userData.professionalProfile?.instagram || "",
      aboutMe: userData.professionalProfile?.aboutMe || "",

      weight: userData.professionalProfile?.weight || "",
      motherTongue: userData.professionalProfile?.motherTongue || "",
      religion: userData.basicProfile?.religion || "",
      caste: userData.basicProfile?.caste || "",
      subCaste: userData.professionalProfile?.subCaste || "",
      diet: userData.professionalProfile?.diet || "",
      gallery: userData.professionalProfile?.gallery || [],
      rasi: userData.professionalProfile?.astrology?.rasi || "",
      natchathiram: userData.professionalProfile?.astrology?.natchathiram || "",
      gothram: userData.professionalProfile?.astrology?.gothram || "",
      laknam: userData.professionalProfile?.astrology?.laknam || "",
      dosham: userData.professionalProfile?.astrology?.dosham || "",
      kujaDosham: userData.professionalProfile?.astrology?.kujaDosham || "",
      min_age: userData.preference?.min_age || userData.min_age || "",
      max_age: userData.preference?.max_age || userData.max_age || "",
      marital_status: userData.preference?.marital_status || userData.marital_status || [],
      mother_tongue: userData.preference?.mother_tongue || userData.mother_tongue || [],
      religion_pref: userData.preference?.religion || userData.religion_pref || [],
      gender_preference: userData.preference?.gender_preference || userData.gender_preference || [],
      caste_pref: userData.preference?.caste || userData.caste_pref || [],
      district_preference: userData.preference?.district || userData.district_preference || [],
      state_preference: userData.preference?.state || userData.state_preference || [],

      sub_caste: userData.preference?.sub_caste || userData.sub_caste || [],
      education_level: userData.preference?.education_level || userData.education_level || [],
      city_pref: userData.preference?.city || userData.city_pref || [],
      family_status: userData.preference?.family_status || userData.family_status || [],
      family_type: userData.preference?.family_type || userData.family_type || [],
      family_values: userData.preference?.family_values || userData.family_values || [],
      about_partner: userData.preference?.about_partner || userData.about_partner || "",
      gallery: userData.professionalProfile.gallery,
      height_range: {
        min: userData.preference?.height_range?.min || "",
        max: userData.preference?.height_range?.max || ""
      },
      zodiac: userData.zodiac || {
        rasi: {
          House_1: [], House_2: [], House_3: [], House_4: [], House_5: [], House_6: [],
          House_7: [], House_8: [], House_9: [], House_10: [], House_11: [], House_12: []
        },
        navamsa: {
          House_1: [], House_2: [], House_3: [], House_4: [], House_5: [], House_6: [],
          House_7: [], House_8: [], House_9: [], House_10: [], House_11: [], House_12: []
        }
      },
      isVerified: userData.isVerified || false
    };

    setUserManagementFormData(formData);

    if (isEditMode) {
      setOriginalMobileNumber(userData.basicProfile?.mobile_number || "");
    }
  };

  // Handle mobile number input with validation
  const handleMobileNumberChange = (value) => {
    const numericValue = value.replace(/\D/g, '').slice(0, 10);

    setUserManagementFormData(prev => ({ ...prev, mobile_number: numericValue }));

    if (numericValue.length === 10) {
      setFieldErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.mobile_number;
        return newErrors;
      });
    } else {
      setFieldErrors(prev => ({
        ...prev,
        mobile_number: numericValue ? 'Mobile number must be exactly 10 digits' : 'Mobile number is required'
      }));
    }
  };


  // ===================== CROPPER STATES =====================
  const [cropModalOpen, setCropModalOpen] = useState(false);
  const [cropImageSrc, setCropImageSrc] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [cropType, setCropType] = useState(""); // "profile" or "gallery"
  const [isFullImageCrop, setIsFullImageCrop] = useState(false); // full image vs fixed aspect crop
  const [mediaSize, setMediaSize] = useState(null); // natural size of the image being cropped

  // Default fixed aspect ratio per image type
  const getDefaultAspect = (type) => (type === "profile" ? 1 / 1 : 4 / 3);

  // Aspect ratio actually applied to the Cropper based on the selected mode
  const activeCropAspect =
    isFullImageCrop && mediaSize
      ? mediaSize.naturalWidth / mediaSize.naturalHeight
      : getDefaultAspect(cropType);

  // Circular mask only makes sense for the fixed "Square" profile crop
  const activeCropShape = !isFullImageCrop && cropType === "profile" ? "round" : "rect";


  const [plans, setPlans] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState(null);
  useEffect(() => {
    const getPlans = async () => {
      try {
        const response = await getWithoutTokenAPI(`/subscriptionPlan/get_all`);
        setPlans(response?.data || []);
      } catch (err) {
        console.log("Plan fetch error", err);
      }
    };
    getPlans();
  }, []);



  const handleImageUpload = async (file, type) => {
    if (!file) return;

    const token = localStorage.getItem('matrimony_token');
    const AdminId = localStorage.getItem('matrimony_userId');
    if (!token) {
      showToast('Authentication token not found. Please login again.', 'error');
      return;
    }

    try {
      setUploadingImage(true);
      const userId = id;

      const formData = new FormData();
      formData.append('file', file);
      formData.append('type', type);

      const response = await fetch(`${BASE_URL}/document/upload?type=${type}&userId=${userId}&adminId=${AdminId}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Upload failed with status ${response.status}`);
      }

      const result = await response.json();
      console.log('Upload success result:', result);

      // FIX: data is an array, get first item
      const fileData = result?.data?.[0];
      if (!fileData?.url || !fileData?.documentId) {
        throw new Error('No image URL or documentId returned in response');
      }

      const imageUrl = fileData.url;
      const documentId = fileData.documentId;

      setUserManagementFormData(prev => {
        const galleryArr = Array.isArray(prev.gallery) ? prev.gallery : [];
        const documentIdArr = Array.isArray(prev.documentId) ? prev.documentId : [];

        if (type === "profile") {
          return { ...prev, profileImage: imageUrl };
        } else if (type === "gallery") {
          return {
            ...prev,
            gallery: [...galleryArr, {url : imageUrl}],
            documentId: [...documentIdArr, documentId],
          };
        }
        return prev;
      });

      showToast(
        type === "profile"
          ? 'Profile image uploaded successfully!'
          : 'Gallery image uploaded successfully!',
        'success'
      );

    } catch (error) {
      console.error('Image upload error:', error);
      const msg = error.message;
      if (msg.includes('Failed to fetch')) {
        showToast('Network error: Cannot connect to server.', 'error');
      } else if (msg.includes('401')) {
        showToast('Authentication failed. Please login again.', 'error');
      } else if (msg.includes('413')) {
        showToast('Image file is too large. Please select a smaller image.', 'error');
      } else if (msg.includes('415')) {
        showToast('Unsupported file type. Please use JPEG, PNG, or GIF.', 'error');
      } else {
        showToast(`Upload failed: ${msg}`, 'error');
      }
    } finally {
      setUploadingImage(false);
    }
  };


  const handleFileChange = (e, type) => {
    const file = e.target.files[0];
    if (!file) return;

    const validTypes = ["image/jpeg", "image/jpg", "image/png", "image/gif"];
    const maxSize = 5 * 1024 * 1024;

    if (!validTypes.includes(file.type)) {
      showToast("Please select a valid image file (JPEG, PNG, GIF)", "error");
      return;
    }

    if (file.size > maxSize) {
      showToast("Image size should be less than 5MB", "error");
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      setCropImageSrc(reader.result);
      setCropType(type);
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      setIsFullImageCrop(false);
      setMediaSize(null);
      setCropModalOpen(true);
    };
    reader.readAsDataURL(file);
  };


  const getCroppedImg = async (imageSrc, croppedAreaPixels) => {
    const img = document.createElement("img");
    img.src = imageSrc;

    await new Promise((resolve) => (img.onload = resolve));

    const canvas = document.createElement("canvas");
    canvas.width = croppedAreaPixels.width;
    canvas.height = croppedAreaPixels.height;

    const ctx = canvas.getContext("2d");
    ctx.drawImage(
      img,
      croppedAreaPixels.x,
      croppedAreaPixels.y,
      croppedAreaPixels.width,
      croppedAreaPixels.height,
      0,
      0,
      croppedAreaPixels.width,
      croppedAreaPixels.height
    );

    return new Promise((resolve) => {
      canvas.toBlob((blob) => resolve(blob), "image/jpeg");
    });
  };

  const handleApplyCrop = async () => {
    try {
      const croppedBlob = await getCroppedImg(cropImageSrc, croppedAreaPixels);

      const croppedFile = new File([croppedBlob], "cropped.jpg", {
        type: "image/jpeg",
      });

      await handleImageUpload(croppedFile, cropType);

      setCropModalOpen(false);
      setCropImageSrc(null);
      setIsFullImageCrop(false);
      setMediaSize(null);

    } catch (err) {
      console.error(err);
      showToast("Failed to crop image", "error");
    }
  };

  const handleUploadButtonClick = () => {
    const fileInput = document.createElement('input');
    fileInput.type = 'file';
    fileInput.accept = 'image/*';
    fileInput.click();
  };

  const loadUserData = async () => {
    const token = localStorage.getItem('matrimony_token');
    if (!token) {
      showToast('Authentication token not found. Please login again.', 'error');
      return;
    }

    setLoadingUserData(true);
    try {
      console.log("Loading user data for ID:", id);

      const endpoints = [
        `/admin/get-user-profile/${id}`,
        `/profile/get_all/${id}`,
        `/users/${id}`,
        `/user/${id}`,
        `/admin/users/${id}`
      ];

      let response = null;
      let lastError = null;

      for (const endpoint of endpoints) {
        try {
          console.log("Trying endpoint:", endpoint);
          response = await getWithTokenAPI(token, `/profile/get_all/${id}`);

          console.log("Success with endpoint:", response);
          if (response && (response.statusCode === 200 || response.success || response.data || (Array.isArray(response) && response.length > 0))) {

            break;
          } else if (response && response.error) {
            console.log(`Endpoint ${endpoint} returned error:`, response.error);
            lastError = new Error(response.message || response.error);
            continue;
          }
        } catch (error) {
          console.log(`Endpoint ${endpoint} failed:`, error.message);
          lastError = error;
          continue;
        }
      }

      if (!response || (response.error && !response.data)) {
        throw lastError || new Error('All endpoints failed. Please check if the user exists and you have proper permissions.');
      }


      let userData = null;

      if (response.data) {
        userData = response.data;
      } else if (response.user) {
        userData = response.user;
      } else if (Array.isArray(response) && response.length > 0) {
        userData = response[0];
      } else if (response.profile) {
        userData = response.profile;
      } else {
        userData = response;
      }

      if (!userData) {
        throw new Error('No user data found in response');
      }

      console.log("User data to load:", userData);
      setupUserManagementEditForm(userData);

    } catch (error) {
      console.error("Load user error details:", {
        message: error.message,
        stack: error.stack,
        id: id
      });

      if (error.message.includes('Failed to fetch') || error.message.includes('Network Error')) {
        showToast("Network error: Cannot connect to server. Please check if the backend server is running on localhost:8013.", 'error');
      } else if (error.message.includes('401') || error.message.includes('Unauthorized')) {
        showToast("Authentication failed. Please login again.", 'error');
      } else if (error.message.includes('404') || error.message.includes('Not Found')) {
        showToast("User not found. The user ID might be invalid or the user has been deleted.", 'error');
      } else if (error.message.includes('All endpoints failed')) {
        showToast("Unable to load user data. Please check:\n1. Backend server is running\n2. API endpoints are correct\n3. You have proper permissions", 'error');
      } else {
        showToast(error.message || "Failed to load user data. Please try again.", 'error');
      }
    } finally {
      setLoadingUserData(false);
    }
  };

  const handleUserManagementInputChange = (field, value) => {
    handleFieldChange(field, value);
  };

  const handleUserManagementHeightRangeChange = (field, value) => {
    setUserManagementFormData(prev => ({
      ...prev,
      height_range: {
        ...prev.height_range,
        [field]: value
      }
    }));
  };

  // Handle array input changes (comma separated)
  const handleUserManagementArrayChange = (field, value) => {
    if (typeof value === 'string') {
      const arrayValue = value.split(',')
        .map(item => item.trim())
        .filter(item => item !== '');
      setUserManagementFormData(prev => ({
        ...prev,
        [field]: arrayValue
      }));
    } else {
      setUserManagementFormData(prev => ({
        ...prev,
        [field]: Array.isArray(value) ? value : [value]
      }));
    }
  };

  const formatDateToDDMMYYYY = (dateString) => {
    if (!dateString) return '';

    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        console.warn('Invalid date:', dateString);
        return '';
      }

      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();

      return `${day}-${month}-${year}`;
    } catch (error) {
      console.error('Date formatting error:', error);
      return '';
    }
  };

  const validateForm = () => {
    const errors = {};

    if (!userManagementFormData.name?.trim()) errors.name = "Name is required";
    if (!userManagementFormData.gender?.trim()) errors.gender = "Gender is required";
    if (!userManagementFormData.age || userManagementFormData.age < 18)
      errors.age = "Valid age (18+) is required";
    if (!userManagementFormData.mobile_number?.trim())
      errors.mobile_number = "Mobile number is required";
    else if (userManagementFormData.mobile_number.length !== 10)
      errors.mobile_number = "Mobile number must be exactly 10 digits";
    if (!userManagementFormData.country_code?.trim())
      errors.country_code = "Country code is required";
    if (!userManagementFormData.status?.trim())
      errors.status = "Status is required";
    if (!userManagementFormData.dob?.trim())
      errors.dob = "Date of birth is required";
    if (!userManagementFormData.district?.trim()) errors.district = "District is required";
    if (!userManagementFormData.maritalStatus?.trim())
      errors.maritalStatus = "Marital status is required";
    if (
      !userManagementFormData.gender_preference
    ) {
      errors.gender_preference = "Please select gender preference";
    }
    // Professional Info
    if (!userManagementFormData.education?.trim())
      errors.education = "Education is required";
    if (userManagementFormData.height && userManagementFormData.height < 100)
      errors.height = "Height must be at least 100 cm";
    if (userManagementFormData.weight && userManagementFormData.weight < 30)
      errors.weight = "Weight must be at least 30 kg";
    if (!userManagementFormData.motherTongue?.trim())
      errors.motherTongue = "Mother tongue is required";
    if (!userManagementFormData.diet?.trim()) errors.diet = "Diet selection is required";
    if (!userManagementFormData.aboutMe?.trim())
      errors.aboutMe = "About Me is required";
    else if (userManagementFormData.aboutMe.length < 10)
      errors.aboutMe = "Please enter at least 10 characters";
    if (!userManagementFormData.height) {
      errors.height = "Height is required";
    } else if (parseInt(userManagementFormData.height) < 100) {
      errors.height = "Height must be at least 100 cm";
    }

    if (
      (userManagementFormData.maritalStatus === "Divorced" || userManagementFormData.maritalStatus === "Widowed")
    ) {
      if (
        userManagementFormData.child_count === undefined ||
        userManagementFormData.child_count === null ||
        userManagementFormData.child_count === ""
      ) {
        errors.child_count = "Child count is required";
      } else if (parseInt(userManagementFormData.child_count) < 0) {
        errors.child_count = "Child count cannot be negative";
      }
    }


    if (
      userManagementFormData.min_age &&
      userManagementFormData.max_age &&
      parseInt(userManagementFormData.min_age) > parseInt(userManagementFormData.max_age)
    ) {
      errors.max_age = "Maximum age must be greater than minimum age";
    }

    if (!userManagementFormData.siblings?.trim()) {
      errors.siblings = "Siblings information is required";
    } else if (userManagementFormData.siblings.trim().length < 5) {
      errors.siblings = "Please enter at least 5 characters for siblings info";
    }

    if (!userManagementFormData.height_range?.min) {
      errors.height_range_min = "Minimum height is required";
    } else if (parseInt(userManagementFormData.height_range.min) < 100) {
      errors.height_range_min = "Minimum height must be at least 100 cm";
    }

    if (!userManagementFormData.height_range?.max) {
      errors.height_range_max = "Maximum height is required";
    } else if (parseInt(userManagementFormData.height_range.max) < 100) {
      errors.height_range_max = "Maximum height must be at least 100 cm";
    }

    if (
      userManagementFormData.height_range?.min &&
      userManagementFormData.height_range?.max &&
      parseInt(userManagementFormData.height_range.min) > parseInt(userManagementFormData.height_range.max)
    ) {
      errors.height_range_max = "Maximum height must be greater than or equal to minimum height";
    }

    if (!userManagementFormData.religion_pref || !userManagementFormData.religion_pref.length)
      errors.religion_pref = "Please select at least one religion";
    if (!userManagementFormData.caste_pref || !userManagementFormData.caste_pref.length)
      errors.caste_pref = "Please select at least one caste";
    if (!userManagementFormData.state_preference)
      errors.state_preference = "State preference is required";
    if (!userManagementFormData.district_preference || !userManagementFormData.district_preference.length)
      errors.district_preference = "Please select at least one district";


    if (!userManagementFormData.religion?.trim()) {
      errors.religion = "Religion is required";
    }

    if (!userManagementFormData.caste?.trim()) {
      errors.caste = "Caste is required";
    }

    if (!userManagementFormData.state?.trim()) {
      errors.state = "State is required";
    }

    if (userManagementFormData.sub_caste) {
      const subCasteArray = Array.isArray(userManagementFormData.sub_caste)
        ? userManagementFormData.sub_caste
        : userManagementFormData.sub_caste.split(","); // split if string

      if (subCasteArray.some((sc) => !sc || sc.trim().length < 2)) {
        errors.sub_caste = "Enter valid sub-caste values";
      }
    }

    if (userManagementFormData.education_level) {
      const educationArray = Array.isArray(userManagementFormData.education_level)
        ? userManagementFormData.education_level
        : userManagementFormData.education_level.split(","); // split if string

      if (educationArray.some((ed) => !ed || ed.trim().length < 2)) {
        errors.education_level = "Enter valid education level values";
      }
    }


    if (!userManagementFormData.family_type || !userManagementFormData.family_type.length) {
      errors.family_type = "Please select at least one family type";
    }

    if (!userManagementFormData.family_values || !userManagementFormData.family_values.length) {
      errors.family_values = "Please select at least one family value";
    }

    if (!userManagementFormData.mother_tongue) {
      errors.preferencesmother_tongue = "Please select at least one mother tongue";
    }

    if (!userManagementFormData.family_status || !userManagementFormData.family_status.length) {
      errors.family_status = "Please select at least one family status";
    }

    if (!userManagementFormData.about_partner?.trim()) {
      errors.about_partner = "About Partner is required";
    } else if (userManagementFormData.about_partner.trim().length < 10) {
      errors.about_partner = "Please enter at least 10 characters for About Partner";
    }
    // Set errors in state
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };


  const showSuccessMessage = () => {
    const message = isEditMode ? 'User profile updated successfully!' : 'User profile created successfully!';

    Swal.fire({
      icon: 'success',
      title: 'Success!',
      text: message,
      confirmButtonText: 'OK',
      confirmButtonColor: '#28a745',
      timer: 3000,
      timerProgressBar: true
    }).then(() => {
      navigate('/admin/user-management');
    });
  };

  const showErrorMessage = (message) => {
    Swal.fire({
      icon: 'error',
      title: 'Error',
      text: message,
      confirmButtonText: 'OK',
      confirmButtonColor: '#dc3545'
    });
  };

  const showLoadingAlert = () => {
    const message = isEditMode ? 'Updating User...' : 'Creating User...';
    Swal.fire({
      title: message,
      text: 'Please wait...',
      allowOutsideClick: false,
      didOpen: () => {
        Swal.showLoading();
      }
    });
  };

  const closeAlert = () => {
    Swal.close();
  };

  const resetUserManagementForm = () => {
    setUserManagementFormData({
      mobile_number: "",
      country_code: "+91",
      name: "",
      gender: "",
      dob: "",
      age: "",
      city: "",
      district: "",
      maritalStatus: "",
      profileImage: "",
      status: "Active",
      education: "",
      profession: "",
      income: "",
      height: "",
      weight: "",
      motherTongue: "",
      religion: "",
      caste: "",
      subCaste: "",
      diet: "",
      gallery: [],
      rasi: "",
      natchathiram: "",
      gothram: "",
      laknam: "",
      dosham: "",
      kujaDosham: "",
      min_age: "",
      max_age: "",
      marital_status: [],
      mother_tongue: [],
      religion_pref: [],
      caste_pref: [],
      sub_caste: [],
      education_level: [],
      city_pref: [],
      family_status: [],
      family_type: [],
      family_values: [],
      about_partner: "",
      height_range: { min: "", max: "" },
      zodiac: {
        rasi: {
          House_1: [], House_2: [], House_3: [], House_4: [], House_5: [], House_6: [],
          House_7: [], House_8: [], House_9: [], House_10: [], House_11: [], House_12: []
        },
        navamsa: {
          House_1: [], House_2: [], House_3: [], House_4: [], House_5: [], House_6: [],
          House_7: [], House_8: [], House_9: [], House_10: [], House_11: [], House_12: []
        }
      },
      isVerified: false
    });
    setFieldErrors({});
    setZodiacErrors({});
    setOriginalMobileNumber("");
  };

  // Delete user function
  const handleDeleteUser = async () => {
    if (!isEditMode || !id) {
      showToast('Cannot delete: No user ID found', 'error');
      return;
    }

    const result = await Swal.fire({
      title: 'Are you sure?',
      text: "You won't be able to revert this!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'No, cancel!',
      reverseButtons: true
    });

    if (result.isConfirmed) {
      const token = localStorage.getItem('matrimony_token');
      if (!token) {
        showToast("Authentication token not found. Please login again.", 'error');
        return;
      }

      setUserManagementCreateLoading(true);
      try {
        console.log("Deleting user with ID:", id);

        const endpoints = [
          `/admin/delete-user/${id}`,
          `/profile/delete/${id}`,
          `/users/${id}`,
          `/user/${id}`
        ];

        let response = null;
        let lastError = null;

        for (const endpoint of endpoints) {
          try {
            console.log("Trying delete endpoint:", endpoint);
            response = await deleteWithTokenAPI(token, endpoint);

            if (response && (response.statusCode === 200 || response.success || response.message)) {
              console.log("Success with delete endpoint:", endpoint, response);
              break;
            }
          } catch (error) {
            console.log(`Delete endpoint ${endpoint} failed:`, error.message);
            lastError = error;
            continue;
          }
        }

        if (!response || response.error) {
          throw lastError || new Error('Failed to delete user');
        }

        showToast('User deleted successfully!', 'success');

        setTimeout(() => {
          navigate('/admin/user-management');
        }, 1500);

      } catch (error) {
        console.error("Delete user error:", error);
        showToast(error.message || 'Failed to delete user', 'error');
      } finally {
        setUserManagementCreateLoading(false);
      }
    }
  };

  // Function to update mobile number via API
  const updateMobileNumber = async (oldMobileNumber, newMobileNumber) => {
    try {
      const token = localStorage.getItem('matrimony_token');
      if (!token) {
        throw new Error("Authentication token not found");
      }

      const payload = {
        old_mobile_number: oldMobileNumber,
        new_mobile_number: newMobileNumber
      };

      console.log("Updating mobile number with payload:", payload);

      const response = await putWithTokenAPI(token, '/update-mobile-number', payload);
      console.log('updateMobileNumber response:', response);

      return response;
    } catch (error) {
      console.error('Error updating mobile number:', error);
      throw error;
    }
  };

  // Block/Unblock user functions
  const handleBlockUser = async () => {
    if (!isEditMode || !id) {
      showToast('Cannot block: No user ID found', 'error');
      return;
    }

    const result = await Swal.fire({
      title: 'Are you sure?',
      text: "This will block the user account!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, block user!',
      cancelButtonText: 'No, cancel!',
      reverseButtons: true
    });

    if (result.isConfirmed) {
      const token = localStorage.getItem('matrimony_token');
      if (!token) {
        showToast("Authentication token not found. Please login again.", 'error');
        return;
      }

      setBlockLoading(true);
      try {
        const payload = {
          userId: id
        };

        console.log("Blocking user with payload:", payload);

        const response = await putWithTokenAPI(token, '/user/block', payload);
        console.log('Block user response:', response);

        if (response && (response.statusCode === 200 || response.success)) {
          showToast('User blocked successfully!', 'success');
          // Update the local status
          setUserManagementFormData(prev => ({
            ...prev,
            status: 'Blocked'
          }));
        } else {
          throw new Error(response?.message || 'Failed to block user');
        }
      } catch (error) {
        console.error("Block user error:", error);
        showToast(error.message || 'Failed to block user', 'error');
      } finally {
        setBlockLoading(false);
      }
    }
  };

  const removeGalleryImage = async (index) => {
    console.log(index, "index")
    const token = localStorage.getItem("matrimony_token");

    if (
      !Array.isArray(userManagementFormData.gallery) ||
      !Array.isArray(userManagementFormData.documentId) ||
      !userManagementFormData.documentId[index]
    ) {
      showToast("Document ID not found for this image!", "error");
      return;
    }

    const documentIdToDelete = userManagementFormData.documentId[index];

    try {
      const response = await deleteWithTokenAPI(
        token,
        `/document/delete/${documentIdToDelete}`
      );

      if (response?.statusCode === 200) {
        showToast("Image deleted successfully!", "success");

        // Remove image and documentId safely
        setUserManagementFormData((prev) => ({
          ...prev,
          gallery: prev.gallery.filter((_, i) => i !== index),
          documentId: prev.documentId.filter((_, i) => i !== index),
        }));
      } else {
        showToast("Failed to delete image from server.", "error");
      }
    } catch (error) {
      console.error(error);
      showToast("Something went wrong while deleting!", "error");
    }
  };



  const handleUnblockUser = async () => {
    if (!isEditMode || !id) {
      showToast('Cannot unblock: No user ID found', 'error');
      return;
    }

    const result = await Swal.fire({
      title: 'Are you sure?',
      text: "This will unblock the user account!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#28a745',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, unblock user!',
      cancelButtonText: 'No, cancel!',
      reverseButtons: true
    });

    if (result.isConfirmed) {
      const token = localStorage.getItem('matrimony_token');
      if (!token) {
        showToast("Authentication token not found. Please login again.", 'error');
        return;
      }

      setBlockLoading(true);
      try {
        const payload = {
          userId: id
        };

        console.log("Unblocking user with payload:", payload);

        const response = await putWithTokenAPI(token, '/user/unblock', payload);
        console.log('Unblock user response:', response);

        if (response && (response.statusCode === 200 || response.success)) {
          showToast('User unblocked successfully!', 'success');
          // Update the local status
          setUserManagementFormData(prev => ({
            ...prev,
            status: 'Active'
          }));
        } else {
          throw new Error(response?.message || 'Failed to unblock user');
        }
      } catch (error) {
        console.error("Unblock user error:", error);
        showToast(error.message || 'Failed to unblock user', 'error');
      } finally {
        setBlockLoading(false);
      }
    }
  };


  const transformHouseData = (houseData) => {
    const transformed = {};
    Object.keys(houseData).forEach(house => {
      if (houseData[house].length > 0 && typeof houseData[house][0] === 'string') {
        transformed[house] = houseData[house][0].split(',').filter(item => item.trim() !== '');
      } else {
        transformed[house] = houseData[house];
      }
    });
    return transformed;
  };

  const handleUserManagementCreate = async () => {
    // if (!validateForm()) {
    //   const firstErrorField = Object.keys(fieldErrors)[0];
    //   if (firstErrorField) {
    //     const element = document.getElementById(`usermgmt_${firstErrorField}`);
    //     if (element) {
    //       element.scrollIntoView({ behavior: 'smooth', block: 'center' });
    //       element.focus();
    //     }
    //   }
    //   showToast('Please fix the validation errors before submitting.', 'error');
    //   return;
    // }

    const token = localStorage.getItem('matrimony_token');

    if (!token) {
      showToast("Authentication token not found. Please login again.", 'error');
      return;
    }

    setUserManagementCreateLoading(true);
    showLoadingAlert();

    try {
      const mobileNumberChanged = isEditMode &&
        originalMobileNumber &&
        userManagementFormData.mobile_number !== originalMobileNumber;

      if (mobileNumberChanged) {
        console.log(`Mobile number changed from ${originalMobileNumber} to ${userManagementFormData.mobile_number}`);

        try {
          const mobileUpdateResponse = await updateMobileNumber(
            originalMobileNumber,
            userManagementFormData.mobile_number
          );

          if (mobileUpdateResponse && mobileUpdateResponse.statusCode === 200) {
            console.log("Mobile number updated successfully");
            showToast('Mobile number updated successfully!', 'success');
          } else {
            console.warn("Mobile number update may have failed:", mobileUpdateResponse);
            showToast('Mobile number update may have failed', 'warning');
          }
        } catch (mobileError) {
          console.error("Failed to update mobile number:", mobileError);
          showToast('Profile updated but mobile number change failed', 'warning');
        }
      }

      const basicProfile = {
        name: userManagementFormData.name,
        gender: userManagementFormData.gender,
        dob: formatDateToDDMMYYYY(userManagementFormData.dob),
        mobile_number: userManagementFormData.mobile_number,
        age: parseInt(userManagementFormData.age) || 0,
        state: userManagementFormData.state,
        district: userManagementFormData.district,
        maritalStatus: userManagementFormData.maritalStatus,
        status: userManagementFormData.status || "Active",
        profileImage: userManagementFormData.profileImage,
        child_count: userManagementFormData.child_count,
        religion: userManagementFormData.religion,
        caste: userManagementFormData.caste,
      };

      if (!isEditMode) {
        basicProfile.user_id = "";
      }

      // FIXED: Ensure height and weight have minimum values
      const professionalProfile = {
        education: userManagementFormData.education,
        profession: userManagementFormData.profession,
        income: userManagementFormData.income ? parseInt(userManagementFormData.income) : 0,
        height: userManagementFormData.height ? parseInt(userManagementFormData.height) : 0,
        weight: userManagementFormData.weight ? parseInt(userManagementFormData.weight) : 0,
        motherTongue: userManagementFormData.motherTongue,
        documentId: userManagementFormData.documentId,
        father_name: userManagementFormData.father_name,
        mother_name: userManagementFormData.mother_name,
        siblings: userManagementFormData.siblings,
        property_details: userManagementFormData.property_details,
        instagram: userManagementFormData.instagram,
        aboutMe: userManagementFormData.aboutMe,


        subCaste: userManagementFormData.subCaste,
        diet: userManagementFormData.diet,
        profileImage: userManagementFormData.profileImage,
        gallery: (userManagementFormData.gallery || []).map(item =>
          typeof item === "string" ? item : item.url
        ),
        astrology: {
          rasi: userManagementFormData.rasi,
          natchathiram: userManagementFormData.natchathiram,
          gothram: userManagementFormData.gothram,
          laknam: userManagementFormData.laknam,
          dosham: userManagementFormData.dosham || "No", // FIXED: Ensure default value
          kujaDosham: userManagementFormData.kujaDosham || "No" // FIXED: Ensure default value
        }
      };

      // FIXED: Apply minimum values for height and weight
      if (professionalProfile.height < 100) {
        professionalProfile.height = 100;
      }
      if (professionalProfile.weight < 30) {
        professionalProfile.weight = 30;
      }

      const payload = {
        email: userManagementFormData.email,
        password: userManagementFormData.password,
        mobile_number: userManagementFormData.mobile_number,
        country_code: userManagementFormData.country_code,
        basicProfile: basicProfile,
        professionalProfile: professionalProfile,

        preference: {
          min_age: userManagementFormData.min_age ? parseInt(userManagementFormData.min_age) : 18,
          max_age: userManagementFormData.max_age ? parseInt(userManagementFormData.max_age) : 40,
          marital_status: userManagementFormData.marital_status || [],
          mother_tongue: userManagementFormData.mother_tongue || [],
          religion: userManagementFormData.religion_pref || [],
          gender_preference: userManagementFormData.gender_preference || [],
          caste: userManagementFormData.caste_pref || [],
          sub_caste: userManagementFormData.sub_caste || [],
          education_level: userManagementFormData.education_level || [],
          city: userManagementFormData.city_pref || [],
          family_status: userManagementFormData.family_status || [],
          family_type: userManagementFormData.family_type || [],
          family_values: userManagementFormData.family_values || [],
          about_partner: userManagementFormData.about_partner,
          state: userManagementFormData.state_preference,
          district: userManagementFormData.district_preference,
          height_range: {
            min: userManagementFormData.height_range?.min
              ? parseInt(userManagementFormData.height_range.min)
              : 150,
            max: userManagementFormData.height_range?.max
              ? parseInt(userManagementFormData.height_range.max)
              : 180
          }
        },


        zodiac: {
          rasi: transformHouseData(userManagementFormData.zodiac.rasi),
          navamsa: transformHouseData(userManagementFormData.zodiac.navamsa)
        },
        isVerified: userManagementFormData.isVerified || false
      };

      if (mobileNumberChanged) {
        payload.old_mobile_number = originalMobileNumber;
        payload.new_mobile_number = userManagementFormData.mobile_number;
      }

      console.log("Sending payload to admin update API:", payload);

      let response;
      if (isEditMode) {
        response = await putWithTokenAPI(
          token,
          `/profile/update/${id}`,
          payload
        );
        console.log('update response', response);

        if (response?.statusCode === 200) {
          closeAlert();
          showToast('User updated successfully!', 'success');
          setTimeout(() => {
            navigate('/admin/user-management');
          }, 1500);
        } else {
          throw new Error(response?.message || 'Failed to update user');
        }
      } else {
        response = await postWithTokenAPI(
          token,
          '/admin/create-user-profile',
          payload
        );
        console.log("profile create Response:", response);

        if (response?.statusCode === 200) {
          closeAlert();
          showToast('User created successfully!', 'success');
          resetUserManagementForm();
          setsubscriptions(true)
          setSubscriptionsUserId(response.data.user_id)
          setActiveTab("subscriptions")
          // setTimeout(() => {
          //   navigate('/admin/user-management');
          // }, 1500);
        } else {
          throw new Error(response?.message || 'Failed to create user');
        }
      }

    } catch (error) {
      console.error(`${isEditMode ? 'Update' : 'Create'} user error:`, error);
      closeAlert();

      const errorMessage = error.message || `Failed to ${isEditMode ? 'update' : 'create'} user profile`;
      const isEmailDuplicate = /email/i.test(errorMessage) && /(exist|already|taken|use)/i.test(errorMessage);
      const isMobileNumberDuplicate = !isEmailDuplicate && /mobile|phone/i.test(errorMessage) && /exist/i.test(errorMessage);

      if (isEmailDuplicate) {
        setFieldErrors(prev => ({ ...prev, email: errorMessage }));
        setActiveTab('mobile-info');

        setTimeout(() => {
          const element = document.getElementById('usermgmt_email');
          if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'center' });
            element.focus();
          }
        }, 100);
      } else if (isMobileNumberDuplicate) {
        setFieldErrors(prev => ({ ...prev, mobile_number: errorMessage }));
        setActiveTab('mobile-info');

        setTimeout(() => {
          const element = document.getElementById('usermgmt_mobile_number');
          if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'center' });
            element.focus();
          }
        }, 100);
      } else {
        showToast(errorMessage, 'error');
      }
    } finally {
      setUserManagementCreateLoading(false);
    }
  };

  const handleBack = () => {
    navigate('/admin/user-management');
  };


  const handleSidebarNavigation = (section) => {
    setActiveSection(section);

    switch (section) {
      case 'content-approval':
        navigate('/admin/content-approval');
        break;
      case 'users':
        navigate('/admin/user-management');
        break;
      case 'dashboard':
        navigate('/admin/dashboard');
        break;
      default:
        navigate(`/admin/${section}`);
    }
  };


  useEffect(() => {
    const initialTab = tab || tabs[0].id;
    if (initialTab !== activeTab) {
      setActiveTab(initialTab);
    }
  }, [tab]);

  const validateMobileInfo = () => {
    const errors = {};

    if (!userManagementFormData.mobile_number?.trim()) {
      errors.mobile_number = "Mobile number is required";
    }

    if (!userManagementFormData.email?.trim()) {
      errors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(userManagementFormData.email)) {
      errors.email = "Invalid email format";
    }

    if (!isEditMode && !userManagementFormData.password) {
      errors.password = "Password is required for new users";
    } else if (userManagementFormData.password && userManagementFormData.password.length < 6) {
      errors.password = "Password must be at least 6 characters";
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const validateBasicInfo = () => {
    const errors = {};
    if (!userManagementFormData.name?.trim()) errors.name = "Name is required";
    if (!userManagementFormData.gender?.trim()) errors.gender = "Gender is required";

    if (!userManagementFormData.dob?.trim())
      errors.dob = "Date of birth is required";
    if (!userManagementFormData.age || userManagementFormData.age < 18)
      errors.age = "Valid age (18+) is required";
    if (!userManagementFormData.state?.trim()) {
      errors.state = "State is required";
    }
    if (!userManagementFormData.district?.trim()) errors.district = "District is required";

    if (!userManagementFormData.religion?.trim()) {
      errors.religion = "Religion is required";
    }
    if (!userManagementFormData.caste?.trim()) {
      errors.caste = "Caste is required";
    }

    if (!userManagementFormData.maritalStatus?.trim())
      errors.maritalStatus = "Marital status is required";

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const validateFamilyDetails = () => {
    const errors = {};

    if (!userManagementFormData.siblings?.trim()) {
      errors.siblings = "Siblings information is required";
    } else if (userManagementFormData.siblings.trim().length < 5) {
      errors.siblings = "Please enter at least 5 characters for siblings info";
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const validateProfessionalInfo = () => {
    const errors = {};

    if (!userManagementFormData.education?.trim())
      errors.education = "Education is required";

    if (!userManagementFormData.height) {
      errors.height = "Height is required";
    } else if (parseInt(userManagementFormData.height) < 100) {
      errors.height = "Height must be at least 100 cm";
    }

    if (!userManagementFormData.motherTongue?.trim())
      errors.motherTongue = "Mother tongue is required";
    if (!userManagementFormData.diet?.trim()) errors.diet = "Diet selection is required";


    if (!userManagementFormData.aboutMe?.trim())
      errors.aboutMe = "About Me is required";
    else if (userManagementFormData.aboutMe.length < 10)
      errors.aboutMe = "Please enter at least 10 characters";

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const validatePartnerPreferences = () => {
    const errors = {};

    if (!userManagementFormData.height_range?.min) {
      errors.height_range_min = "Minimum height is required";
    } else if (parseInt(userManagementFormData.height_range.min) < 100) {
      errors.height_range_min = "Minimum height must be at least 100 cm";
    }

    if (!userManagementFormData.height_range?.max) {
      errors.height_range_max = "Maximum height is required";
    } else if (parseInt(userManagementFormData.height_range.max) < 100) {
      errors.height_range_max = "Maximum height must be at least 100 cm";
    }
    if (!userManagementFormData.religion_pref || !userManagementFormData.religion_pref.length)
      errors.religion_pref = "Please select at least one religion";
    if (
      !userManagementFormData.gender_preference
    ) {
      errors.gender_preference = "Please select gender preference";
    }
    if (!userManagementFormData.caste_pref || !userManagementFormData.caste_pref.length)
      errors.caste_pref = "Please select at least one caste";
    if (!userManagementFormData.state_preference)
      errors.state_preference = "State preference is required";
    if (!userManagementFormData.district_preference || !userManagementFormData.district_preference.length)
      errors.district_preference = "Please select at least one district";
    if (!userManagementFormData.family_status || !userManagementFormData.family_status.length) {
      errors.family_status = "Please select at least one family status";
    }

    if (!userManagementFormData.family_type || !userManagementFormData.family_type.length) {
      errors.family_type = "Please select at least one family type";
    } if (!userManagementFormData.family_values || !userManagementFormData.family_values.length) {
      errors.family_values = "Please select at least one family value";
    }

    if (!userManagementFormData.about_partner?.trim()) {
      errors.about_partner = "About Partner is required";
    } else if (userManagementFormData.about_partner.trim().length < 10) {
      errors.about_partner = "Please enter at least 10 characters for About Partner";
    }
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const validateCurrentTab = () => {
    const tabId = tabs[currentIndex].id;

    switch (tabId) {

      case "mobile-info":
        return validateMobileInfo();

      case "basic-info":
        return validateBasicInfo();

      // case "family-details":
      //   return validateFamilyDetails();

      // case "professional-info":
      //   return validateProfessionalInfo();

      // case "partner-preferences":
      //   return validatePartnerPreferences();

      default:
        return true; // no validation needed
    }
  };


console.log(userManagementFormData.gallery, 'userManagementFormData.gallery')

  const currentIndex = tabs.findIndex(t => t.id === activeTab);

  const goNext = () => {
    const isValid = validateCurrentTab();
    if (!isValid) {
      return; // STOP – DO NOT GO TO NEXT TAB
    }

    if (currentIndex < tabs.length - 1) {
      handleTabChange(tabs[currentIndex + 1].id);
    }
  };

  const goPrev = () => {
    if (currentIndex > 0) {
      handleTabChange(tabs[currentIndex - 1].id);
    }
  };

  const handleTabChange = (tabId) => {

    const isValid = validateCurrentTab();
    if (!isValid) {
      return;
    }

    if (tabId === activeTab) {
      console.log('Tab already active:', tabId);
      return; // Prevent unnecessary updates
    }

    console.log('Changing tab to:', tabId);
    setActiveTab(tabId);

    const basePath = isEditMode
      ? `/admin/user-management/edit/${id}`
      : `/admin/user-management/create`;

    // Only navigate if the URL doesn't already match the desired tab
    if (tab !== tabId) {
      console.log('Navigating to:', `${basePath}/${tabId}`);
      navigate(`${basePath}/${tabId}`);
    } else {
      console.log('URL already matches tab, no navigation needed');
    }
  };

  const [loading, setLoading] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState("");
  const renderFormSection = () => {
    switch (activeTab) {
      case 'mobile-info':
        return (
          <div className="form-section mb-8">
            <h3 className="section-title text-lg font-semibold mb-4 text-gray-800 border-b pb-2">Login Information</h3>
            <div className="form-grid grid grid-cols-1 md:grid-cols-2 gap-4 pt-3">
              <div className="form-field">
                <label htmlFor="usermgmt_country_code" className="form-label block text-sm font-medium text-gray-700 mb-2">
                  Country Code <span style={{ color: 'red' }}>*</span>
                </label>

                <Select
                  id="usermgmt_country_code"
                  options={countryOptions}
                  value={countryOptions.find(opt => opt.value === userManagementFormData.country_code)}
                  onChange={(selected) => handleFieldChange('country_code', selected?.value || '')}
                  isDisabled={userManagementCreateLoading}
                  placeholder="Select country code"
                  className="text-sm"
                  styles={{
                    control: (base, state) => ({
                      ...base,
                      borderColor: fieldErrors.country_code ? 'red' : base.borderColor,
                      boxShadow: state.isFocused ? '0 0 0 2px #4a8ef0' : 'none',
                      borderRadius: '6px',
                    }),
                  }}
                />

                {fieldErrors.country_code && (
                  <p className="text-red-500 text-xs mt-1">{fieldErrors.country_code}</p>
                )}
              </div>
              <div className="form-field">
                <label htmlFor="usermgmt_mobile_number" className="form-label block text-sm font-medium text-gray-700 mb-2 ">{t("userForm.fields.mobileNumber.label")} <span style={{ color: 'red' }}>*</span></label>
                <input
                  id="usermgmt_mobile_number"
                  type="tel"
                  value={userManagementFormData.mobile_number}
                  onChange={(e) => handleMobileNumberChange(e.target.value)}
                  placeholder={t("userForm.fields.mobileNumber.placeholder")}
                  disabled={userManagementCreateLoading}
                  className={`form-input w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${fieldErrors.mobile_number ? 'border-red-500' : 'border-gray-300'
                    }`}
                  maxLength="10"
                  pattern="[0-9]{10}"
                  required
                />
                {fieldErrors.mobile_number && (
                  <p className="text-red-500 text-xs mt-1">{fieldErrors.mobile_number}</p>
                )}
                {isEditMode && originalMobileNumber && userManagementFormData.mobile_number !== originalMobileNumber && (
                  <p className="text-blue-500 text-xs mt-1">
                    {t("userForm.fields.mobileNumber.updateNotice", {
                      oldNumber: originalMobileNumber,
                      newNumber: userManagementFormData.mobile_number
                    })}                  </p>
                )}
              </div>
              <div className="form-field">
                <label htmlFor="usermgmt_email" className="form-label block text-sm font-medium text-gray-700 mb-2 ">
                  Email <span style={{ color: 'red' }}>*</span>
                </label>
                <input
                  id="usermgmt_email"
                  type="email"
                  value={userManagementFormData.email}
                  onChange={(e) => handleFieldChange('email', e.target.value)}
                  placeholder="Enter email address"
                  disabled={userManagementCreateLoading}
                  className={`form-input w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${fieldErrors.email ? 'border-red-500' : 'border-gray-300'}`}
                  required
                />
                {fieldErrors.email && (
                  <p className="text-red-500 text-xs mt-1">{fieldErrors.email}</p>
                )}
              </div>

              <div className="form-field">
                <label htmlFor="usermgmt_password" className="form-label block text-sm font-medium text-gray-700 mb-2 ">
                  Password {isEditMode ? '' : <span style={{ color: 'red' }}>*</span>}
                </label>
                <div className="relative">
                  <input
                    id="usermgmt_password"
                    type={showPassword ? 'text' : 'password'}
                    value={userManagementFormData.password}
                    onChange={(e) => handleFieldChange('password', e.target.value)}
                    placeholder={isEditMode ? "Leave empty to keep current password" : "Enter password"}
                    disabled={userManagementCreateLoading}
                    className={`form-input w-full px-3 py-2 pr-10 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${fieldErrors.password ? 'border-red-500' : 'border-gray-300'}`}
                    required={!isEditMode}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-500 hover:text-gray-700"
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
                {fieldErrors.password && (
                  <p className="text-red-500 text-xs mt-1">{fieldErrors.password}</p>
                )}
              </div>
            </div>
          </div>
        );

      case 'basic-info':
        return (
          <div className="form-section mb-8">
            <h3 className="section-title text-lg font-semibold mb-4 text-gray-800 border-b pb-2">  {t("userForm.BasicuserForm.sections.basicInfo")}</h3>
            <div className="form-grid grid grid-cols-1 md:grid-cols-2 gap-4 pt-3">
              <div className="form-field">
                <label htmlFor="usermgmt_name" className="form-label block text-sm font-medium text-gray-700 mb-2">  {t("userForm.BasicuserForm.fields.name.label")} <span style={{ color: 'red' }}>*</span></label>
                <input
                  id="usermgmt_name"
                  value={userManagementFormData.name}
                  onChange={(e) => handleFieldChange('name', e.target.value)}
                  placeholder={t("userForm.BasicuserForm.fields.name.placeholder")}
                  disabled={userManagementCreateLoading}
                  className={`form-input w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${fieldErrors.name ? 'border-red-500' : 'border-gray-300'
                    }`}
                  required
                />
                {fieldErrors.name && (
                  <p className="text-red-500 text-xs mt-1">{fieldErrors.name}</p>
                )}
              </div>

              <div className="form-field">
                <label htmlFor="usermgmt_gender" className="form-label block text-sm font-medium text-gray-700 mb-2"> {t("userForm.BasicuserForm.fields.gender.label")}  <span style={{ color: 'red' }}>*</span></label>
                <select
                  id="usermgmt_gender"
                  value={userManagementFormData.gender}
                  onChange={(e) => handleFieldChange('gender', e.target.value)}
                  disabled={userManagementCreateLoading}
                  className={`form-select w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${fieldErrors.gender ? 'border-red-500' : 'border-gray-300'
                    }`}
                  required
                >
                  <option value="">{t("userForm.BasicuserForm.fields.gender.placeholder")}</option>
                  <option value="Male">{t("userForm.BasicuserForm.fields.gender.options.male")}</option>
                  <option value="Female">{t("userForm.BasicuserForm.fields.gender.options.female")}</option>
                  <option value="Other">{t("userForm.BasicuserForm.fields.gender.options.other")}</option>
                </select>
                {fieldErrors.gender && (
                  <p className="text-red-500 text-xs mt-1">{fieldErrors.gender}</p>
                )}
              </div>

              <div className="form-field">
                <label htmlFor="usermgmt_dob" className="form-label block text-sm font-medium text-gray-700 mb-2">
                  {t("userForm.BasicuserForm.fields.dob.label")} <span style={{ color: 'red' }}>*</span>
                </label>
                <input
                  id="usermgmt_dob"
                  type="date"
                  value={userManagementFormData.dob}
                  onChange={(e) => handleDOBChange(e.target.value)}
                  disabled={userManagementCreateLoading}
                  className={`form-input w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${fieldErrors.dob ? 'border-red-500' : 'border-gray-300'
                    }`}
                  required
                  max={new Date().toISOString().split('T')[0]} // Prevent future dates
                />
                {fieldErrors.dob && (
                  <p className="text-red-500 text-xs mt-1">{fieldErrors.dob}</p>
                )}

              </div>

              <div className="form-field">
                <label htmlFor="usermgmt_age"
                  className="form-label block text-sm font-medium text-gray-700 mb-2">
                  {t("userForm.BasicuserForm.fields.age.label")}
                  <span style={{ color: 'red' }}>*</span></label>
                <input id="usermgmt_age" type="number" value={userManagementFormData.age}
                  onChange={(e) => handleFieldChange('age', e.target.value)}
                  placeholder="Enter age" min="18" max="100" disabled={userManagementCreateLoading}
                  className={`form-input w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500
                   ${fieldErrors.age ? 'border-red-500' : 'border-gray-300'}`}
                  required />
                {fieldErrors.age && (<p className="text-red-500 text-xs mt-1">{fieldErrors.age}</p>)}
              </div>

              <div className="space-y-2">
                <Label className="text-sm mb-1 block">
                  {t("userForm.BasicuserForm.fields.state.label")}
                </Label>

                <Select
                  value={
                    userManagementFormData
                      ? { value: userManagementFormData.state, label: userManagementFormData.state }
                      : null
                  }
                  onChange={(selectedOption) => handleStateChange(selectedOption, "state")}
                  options={statesOptions}
                  placeholder={t("userForm.BasicuserForm.fields.state.placeholder")}
                  isClearable
                  isDisabled={userManagementCreateLoading}
                />

                {fieldErrors && fieldErrors?.state && (
                  <p className="text-red-500 text-sm">{fieldErrors.state}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label className="text-sm mb-1 block">
                  {t("userForm.BasicuserForm.fields.district.label")}{" "}
                </Label>

                <Select
                  value={
                    userManagementFormData.district
                      ? { value: userManagementFormData.district, label: userManagementFormData.district }
                      : null
                  }
                  onChange={(selectedOption) =>
                    handleFieldChange(
                      "district",
                      selectedOption ? selectedOption.value : ""
                    )
                  }
                  options={districtsOptions}   // <--- from districtList
                  placeholder={t("Select District")}
                  isClearable
                  isDisabled={!selectedState}
                />

                {fieldErrors && fieldErrors?.district && (
                  <p className="text-red-500 text-sm">{fieldErrors.district}</p>
                )}
              </div>

              <div className="form-field">
                <label htmlFor="religion" className="form-label block text-sm font-medium text-gray-700 mb-2">
                  {t("userForm.professionalInfo.religion.label")}
                </label>
                <select
                  id="religion"
                  value={userManagementFormData.religion}
                  onChange={(e) => handleFieldChange('religion', e.target.value)}
                  disabled={userManagementCreateLoading}
                  className={`form-select w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${fieldErrors.religion ? 'border-red-500' : 'border-gray-300'
                    }`}
                >
                  <option value="">{t("userForm.professionalInfo.religion.placeholder")}</option>
                  {religionList.map((religion) => (
                    <option key={religion} value={religion.value}>
                      {religion.label}
                    </option>
                  ))}
                </select>
                {fieldErrors.religion && (
                  <p className="text-red-500 text-xs mt-1">{fieldErrors.religion}</p>
                )}
              </div>

              <div className="form-field">
                <label className="form-label block text-sm font-medium text-gray-700 mb-2">
                  {t("userForm.BasicuserForm.fields.caste.label")}
                  <span style={{ color: "red" }}>*</span>
                </label>

                <Select
                  options={casteOptions}
                  value={casteOptions.find(
                    (item) => item.value === userManagementFormData.caste
                  )}
                  onChange={(selected) =>
                    handleFieldChange("caste", selected.value)
                  }
                  isDisabled={userManagementCreateLoading}
                  placeholder={t("userForm.BasicuserForm.fields.caste.placeholder")}
                  className="text-sm"
                />

                {fieldErrors.caste && (
                  <p className="text-red-500 text-xs mt-1">{fieldErrors.caste}</p>
                )}
              </div>

              <div className="form-field">
                <label htmlFor="usermgmt_maritalStatus" className="form-label block text-sm font-medium text-gray-700 mb-2"> {t("userForm.BasicuserForm.fields.maritalStatus.label")} <span style={{ color: 'red' }}>*</span></label>
                <select
                  id="usermgmt_maritalStatus"
                  value={userManagementFormData.maritalStatus}
                  onChange={(e) => handleFieldChange('maritalStatus', e.target.value)}
                  disabled={userManagementCreateLoading}
                  className={`form-select w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${fieldErrors.maritalStatus ? 'border-red-500' : 'border-gray-300'
                    }`}
                  required
                >
                  <option value="">{t("userForm.BasicuserForm.fields.maritalStatus.placeholder")}</option>
                  <option value="Single">{t("userForm.BasicuserForm.fields.maritalStatus.options.single")}</option>
                  {/* <option value="Married">{t("userForm.BasicuserForm.fields.maritalStatus.options.married")}</option> */}
                  <option value="Divorced">{t("userForm.BasicuserForm.fields.maritalStatus.options.divorced")}</option>
                  <option value="Widowed">{t("userForm.BasicuserForm.fields.maritalStatus.options.widowed")}</option>
                </select>
                {fieldErrors.maritalStatus && (
                  <p className="text-red-500 text-xs mt-1">{fieldErrors.maritalStatus}</p>
                )}
              </div>

              {(userManagementFormData.maritalStatus === "Divorced" || userManagementFormData.maritalStatus === "Widowed") && (
                <div className="form-field">
                  <label htmlFor="usermgmt_dob" className="form-label block text-sm font-medium text-gray-700 mb-2">
                    {t("userForm.BasicuserForm.fields.child_count.label")} <span style={{ color: 'red' }}></span>
                  </label>
                  <input
                    id="usermgmt_child_count"
                    type="number"
                    value={userManagementFormData.child_count || 0}
                    placeholder={t("userForm.BasicuserForm.fields.child_count.placeholder")}
                    onChange={(e) => handleFieldChange('child_count', e.target.value)}
                    disabled={userManagementCreateLoading}
                    className={`form-input w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${fieldErrors.child_count ? 'border-red-500' : 'border-gray-300'
                      }`}
                    required
                  />
                  {fieldErrors.child_count && (
                    <p className="text-red-500 text-xs mt-1">{fieldErrors.child_count}</p>
                  )}
                </div>
              )}

            </div>

            {
              isEditMode && (
                <>
                  <div className="mt-8 mb-6">
                    <label htmlFor="usermgmt_profileImage" className="form-label block text-sm font-medium text-gray-700 mb-3">
                      {t("userForm.BasicuserForm.fields.profileImage.label")}
                    </label>

                    <div className="flex items-center space-x-6">
                      <div className="relative">
                        <div
                          onClick={() => document.getElementById("profileFileInput").click()}
                          style={{
                            width: "150px",
                            height: "150px",
                            borderRadius: "50%",
                            border: "2px solid #e5e7eb",
                            cursor: "pointer",
                            overflow: "hidden",
                            backgroundColor: "#f3f4f6",
                            transition: "border-color 0.2s",
                          }}      >
                          {userManagementFormData.profileImage ? (
                            <img
                              src={userManagementFormData.profileImage}
                              alt="Profile"
                              className="w-full h-full object-contain"
                              onError={(e) => {
                                e.target.src = 'https://via.placeholder.com/80x80?text=Error';
                              }}
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <User className="h-8 w-8 text-gray-400" />
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="flex-1">
                        <div
                          onClick={() => document.getElementById("profileFileInput").click()}
                          style={{
                            border: "1px solid #d1d5db",
                            borderRadius: "0.5rem",
                            padding: "1rem",
                            cursor: "pointer",
                            transition: "border-color 0.2s",
                            width: "250px",
                          }}
                          onMouseEnter={e => e.currentTarget.style.borderColor = "#3b82f6"}
                          onMouseLeave={e => e.currentTarget.style.borderColor = "#d1d5db"}>
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="text-sm font-medium text-gray-900">
                                {userManagementFormData.profileImage
                                  ? "Change profile photo"
                                  : "Upload profile photo"
                                }
                              </p>
                              <p className="text-xs text-gray-500 mt-1">
                                JPG, PNG, WEBP up to 5MB
                              </p>
                            </div>
                            <Upload className="h-5 w-5 text-gray-400" />
                          </div>
                        </div>

                        {/* Upload Status */}
                        {uploadingImage && (
                          <div className="flex items-center space-x-2 mt-2">
                            <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                            <span className="text-xs text-blue-600">Uploading...</span>
                          </div>
                        )}


                      </div>
                    </div>

                    <input
                      type="file"
                      id="profileFileInput"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => handleFileChange(e, "profile")}
                      disabled={userManagementCreateLoading || uploadingImage}
                    />
                  </div>

                  <div className="mt-8 mb-6">
                    {/* Section Label */}
                    <label className="block text-sm font-medium text-gray-700 mb-4">
                      Gallery Photos
                    </label>

                    <div className="space-y-6">

                      <input
                        type="file"
                        id="galleryFileInput"
                        multiple
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => handleFileChange(e, "gallery")}
                      />
                      <div
                        onClick={() => document.getElementById("galleryFileInput").click()}
                        className="cursor-pointer transition-colors duration-200 border border-gray-300 rounded-xl p-4 w-[200px] hover:border-blue-500 bg-white flex flex-col justify-between"
                        onMouseEnter={e => e.currentTarget.style.borderColor = "#3b82f6"}
                        onMouseLeave={e => e.currentTarget.style.borderColor = "#d1d5db"}
                        style={{ width: "38%" }}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex flex-col">
                            <p className="text-sm font-medium text-gray-900">
                              {userManagementFormData.gallery.length > 0 ? "Add more photos" : "Upload gallery photo"}
                            </p>
                            <p className="text-xs text-gray-500 mt-1">
                              JPG, PNG, WEBP up to 5MB
                            </p>
                          </div>
                          <Upload className="h-5 w-5 text-gray-400" />
                        </div>
                      </div>
                      {userManagementFormData.gallery && userManagementFormData.gallery.length > 0 && (
                        <div className="bg-gray-50 rounded-2xl p-5">
                          <div className="flex items-center justify-between mb-4">
                            <h5 className="text-sm font-semibold text-gray-900">
                              Your Photos ({userManagementFormData.gallery.length})
                            </h5>
                          </div>

                          <div className="flex flex-row gap-4 overflow-x-auto py-2">

                            {userManagementFormData.gallery && userManagementFormData.gallery.length > 0 && (
                              <div className="bg-gray-50 rounded-2xl p-5">

                                <div className="flex flex-row gap-4 overflow-x-auto py-2">
                                  {userManagementFormData.gallery.map((img, index) => (
                                    <div
                                      key={index}
                                      className="relative"
                                      style={{
                                        width: "150px",
                                        height: "150px",
                                        borderRadius: "20px",
                                        border: "2px solid #e5e7eb",
                                        cursor: "pointer",
                                        overflow: "hidden",
                                        backgroundColor: "#f3f4f6",
                                        transition: "border-color 0.2s",
                                        position: "relative",
                                      }}
                                    >
                                      <img
                                        src={img.url}
                                        alt={`Gallery ${index + 1}`}
                                        className="w-full h-full object-contain"
                                      />

                                      <button
                                        onClick={() => removeGalleryImage(index)}
                                        className="absolute top-2 right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center text-xs shadow-lg z-10 cursor-pointer"
                                      >
                                        <span className="flex items-center justify-center w-full h-full">×</span>
                                      </button>


                                      <div className="absolute bottom-2 left-2 bg-white/70 px-2 py-0.5 rounded-full text-xs font-semibold text-gray-800 shadow">
                                        {index + 1}
                                      </div>
                                    </div>
                                  ))}
                                </div>


                              </div>
                            )}
                            <div
                              onClick={() => document.getElementById("galleryFileInput").click()}
                              style={{
                                width: "150px",
                                height: "150px",
                                backgroundColor: "#ffffff",
                                border: "2px solid #e5e7eb",
                                borderRadius: "20px",
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                justifyContent: "center",
                                cursor: "pointer",
                                boxShadow: "0 4px 6px rgba(0,0,0,0.1)",
                                transition: "all 0.3s ease",
                                position: "relative",
                              }}
                              onMouseEnter={e => {
                                e.currentTarget.style.borderColor = "#3b82f6";
                                e.currentTarget.style.boxShadow = "0 8px 12px rgba(0,0,0,0.15)";
                              }}
                              onMouseLeave={e => {
                                e.currentTarget.style.borderColor = "#e5e7eb";
                                e.currentTarget.style.boxShadow = "0 4px 6px rgba(0,0,0,0.1)";
                              }}
                            >
                              <Plus style={{ width: "24px", height: "24px", color: "#9ca3af", marginBottom: "8px" }} />
                              <span style={{ fontSize: "12px", color: "#6b7280", textAlign: "center" }}>Add New</span>
                            </div>
                          </div>

                          <input
                            type="file"
                            id="galleryFileInput"
                            multiple
                            accept="image/*"
                            className="hidden"
                            onChange={(e) => handleFileChange(e, "gallery")}
                          />
                        </div>
                      )}

                    </div>
                  </div>
                </>
              )
            }


            {cropModalOpen && (
              <div
                style={{
                  position: "fixed",
                  inset: 0,
                  zIndex: 9998,
                  overflow: "hidden",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <div
                  style={{
                    position: "absolute",
                    top: 0,
                    right: 0,
                    bottom: 0,
                    left: 0,
                    backgroundColor: "rgba(0, 0, 0, 0.75)",
                    backdropFilter: "blur(4px)",
                  }}
                ></div>

                <div
                  className="bg-white p-6 rounded-2xl shadow-2xl relative"
                  style={{
                    width: "95%",
                    maxWidth: "650px",
                    maxHeight: "90vh",
                    zIndex: 9999,
                  }}
                >
                  <h3 className="text-xl font-semibold mb-4">Crop Image</h3>

                  <div className="flex items-center justify-center gap-2 mb-3">
                    <button
                      type="button"
                      onClick={() => {
                        setIsFullImageCrop(false);
                        setCrop({ x: 0, y: 0 });
                        setZoom(1);
                      }}
                      className={`flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium border transition-colors cursor-pointer ${
                        !isFullImageCrop
                          ? "bg-blue-600 text-white border-blue-600"
                          : "bg-white text-gray-600 border-gray-300 hover:bg-gray-100"
                      }`}
                    >
                      <Square className="w-3 h-3" />
                      {cropType === "profile" ? "Square" : "4:3"}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setIsFullImageCrop(true);
                        setCrop({ x: 0, y: 0 });
                        setZoom(1);
                      }}
                      className={`flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium border transition-colors cursor-pointer ${
                        isFullImageCrop
                          ? "bg-blue-600 text-white border-blue-600"
                          : "bg-white text-gray-600 border-gray-300 hover:bg-gray-100"
                      }`}
                    >
                      <CropIcon className="w-3 h-3" />
                      Full Image
                    </button>
                  </div>

                  <div
                    className="relative bg-gray-200 rounded-xl overflow-hidden"
                    style={{ height: "380px" }}
                  >
                    <Cropper
                      image={cropImageSrc}
                      crop={crop}
                      zoom={zoom}
                      aspect={activeCropAspect}
                      cropShape={activeCropShape}
                      showGrid={false}
                      onCropChange={setCrop}
                      onZoomChange={setZoom}
                      onMediaLoaded={(size) => setMediaSize(size)}
                      onCropComplete={(c, pixels) => setCroppedAreaPixels(pixels)}
                    />
                  </div>

                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "flex-end",
                      gap: "0.75rem",
                      marginTop: "1.25rem",
                    }}
                  >
                    <button
                      onClick={() => {
                        setCropModalOpen(false);
                        setCropImageSrc(null);
                        setIsFullImageCrop(false);
                        setMediaSize(null);
                      }}
                      style={{
                        padding: "8px 20px",
                        borderRadius: "8px",
                        backgroundColor: "#e5e7eb",
                        color: "#374151",
                        fontWeight: 500,
                        cursor: "pointer",
                        transition: "all 0.2s",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = "#d1d5db";
                        e.currentTarget.style.boxShadow = "0 2px 6px rgba(0,0,0,0.15)";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = "#e5e7eb";
                        e.currentTarget.style.boxShadow = "none";
                      }}
                    >
                      Cancel
                    </button>

                    <button
                      onClick={handleApplyCrop}
                      style={{
                        padding: "8px 24px",
                        borderRadius: "8px",
                        backgroundColor: "#2563eb",
                        color: "white",
                        fontWeight: 500,
                        cursor: "pointer",
                        transition: "all 0.2s",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = "#1d4ed8";
                        e.currentTarget.style.boxShadow = "0 2px 6px rgba(0,0,0,0.15)";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = "#2563eb";
                        e.currentTarget.style.boxShadow = "none";
                      }}
                    >
                      Apply Crop
                    </button>
                  </div>

                </div>
              </div>
            )}

          </div>
        );


      case 'family-details':
        return (
          <div className="form-section mb-8">
            <h3 className="section-title text-lg font-semibold mb-4 text-gray-800 border-b pb-2">{t("userForm.userTabs.familyDetails")}</h3>
            <div className="form-grid grid grid-cols-1 md:grid-cols-2 gap-4 pt-3">

              {/* Father’s Name */}
              <div className="form-field">
                <label htmlFor="father_name" className="form-label block text-sm font-medium text-gray-700 mb-2">
                  {t("userForm.ProfessionalForm.fields.fatherName.label")} <span style={{ color: 'red' }}>*</span>
                </label>
                <input
                  id="father_name"
                  value={userManagementFormData.father_name}
                  onChange={(e) => handleUserManagementInputChange("father_name", e.target.value)}
                  placeholder={t("userForm.ProfessionalForm.fields.fatherName.placeholder")}
                  disabled={userManagementCreateLoading}
                  className={`form-input w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${fieldErrors.father_name ? "border-red-500" : "border-gray-300"
                    }`}
                  required
                />
                {fieldErrors.father_name && (
                  <p className="text-red-500 text-xs mt-1">{fieldErrors.father_name}</p>
                )}
              </div>

              {/* Mother’s Name */}
              <div className="form-field">
                <label htmlFor="mother_name" className="form-label block text-sm font-medium text-gray-700 mb-2">
                  {t("userForm.ProfessionalForm.fields.motherName.label")} <span style={{ color: 'red' }}>*</span>
                </label>
                <input
                  id="mother_name"
                  value={userManagementFormData.mother_name}
                  onChange={(e) => handleUserManagementInputChange("mother_name", e.target.value)}
                  placeholder={t("userForm.ProfessionalForm.fields.motherName.placeholder")}
                  disabled={userManagementCreateLoading}
                  className={`form-input w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${fieldErrors.mother_name ? "border-red-500" : "border-gray-300"
                    }`}
                  required
                />
                {fieldErrors.mother_name && (
                  <p className="text-red-500 text-xs mt-1">{fieldErrors.mother_name}</p>
                )}
              </div>

              {/* Siblings */}
              <div className="form-field">
                <label htmlFor="siblings" className="form-label block text-sm font-medium text-gray-700 mb-2">
                  {t("userForm.ProfessionalForm.fields.siblings.label")} <span style={{ color: 'red' }}>*</span>
                </label>
                <textarea
                  id="siblings"
                  value={userManagementFormData.siblings}
                  onChange={(e) => handleUserManagementInputChange("siblings", e.target.value)}
                  placeholder={t("userForm.ProfessionalForm.fields.siblings.placeholder")}
                  disabled={userManagementCreateLoading}
                  rows={3}
                  className={`form-input w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${fieldErrors.siblings ? "border-red-500" : "border-gray-300"
                    }`}
                  required
                />
                {fieldErrors.siblings && (
                  <p className="text-red-500 text-xs mt-1">{fieldErrors.siblings}</p>
                )}
              </div>

              {/* Property Details */}
              <div className="form-field">
                <label htmlFor="property_details" className="form-label block text-sm font-medium text-gray-700 mb-2">
                  {t("userForm.ProfessionalForm.fields.propertyDetails.label")} <span style={{ color: 'red' }}>*</span>
                </label>
                <textarea
                  id="property_details"
                  value={userManagementFormData.property_details}
                  onChange={(e) => handleUserManagementInputChange("property_details", e.target.value)}
                  placeholder={t("userForm.ProfessionalForm.fields.propertyDetails.placeholder")}
                  disabled={userManagementCreateLoading}
                  rows={3}
                  className={`form-input w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${fieldErrors.property_details ? "border-red-500" : "border-gray-300"
                    }`}
                  required
                />
                {fieldErrors.property_details && (
                  <p className="text-red-500 text-xs mt-1">{fieldErrors.property_details}</p>
                )}
              </div>


            </div>
          </div>
        );

      case 'professional-info':
        return (
          <div className="form-section mb-8">
            <h3 className="section-title text-lg font-semibold mb-4 text-gray-800 border-b pb-2"> {t("userForm.ProfessionalForm.tabs.professionalInfo.title")}</h3>
            <div className="form-grid grid grid-cols-1 md:grid-cols-2 gap-4 pt-3">


              <div className="form-field">
                <label htmlFor="usermgmt_education" className="form-label block text-sm font-medium text-gray-700 mb-2">{t("userForm.ProfessionalForm.fields.education.label")} <span style={{ color: 'red' }}>*</span></label>
                <select
                  value={userManagementFormData.education}
                  onChange={(e) => handleUserManagementInputChange('education', e.target.value)}
                  disabled={userManagementCreateLoading}
                  className={`form-select w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${fieldErrors.education ? 'border-red-500' : 'border-gray-300'
                    }`}
                  required
                >
                  <option value="">{t("userForm.ProfessionalForm.fields.education.placeholder")}</option>
                  {educationOptions.map((option) => (
                    <option key={option} value={option.value}>{option.label}</option>
                  ))}
                </select>
                {fieldErrors.education && (
                  <p className="text-red-500 text-xs mt-1">{fieldErrors.education}</p>
                )}
              </div>

              <div className="form-field">
                <label htmlFor="usermgmt_profession" className="form-label block text-sm font-medium text-gray-700 mb-2"> {t("userForm.ProfessionalForm.fields.profession.label")} <span style={{ color: 'red' }}>*</span></label>
                <input
                  id="usermgmt_profession"
                  value={userManagementFormData.profession}
                  onChange={(e) => handleUserManagementInputChange('profession', e.target.value)}
                  placeholder={t("userForm.ProfessionalForm.fields.profession.placeholder")}
                  disabled={userManagementCreateLoading}
                  className={`form-input w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${fieldErrors.profession ? 'border-red-500' : 'border-gray-300'
                    }`}
                  required
                />
                {/* {fieldErrors.profession && (
                  <p className="text-red-500 text-xs mt-1">{fieldErrors.profession}</p>
                )} */}
              </div>

              <div className="form-field">
                <label htmlFor="usermgmt_income" className="form-label block text-sm font-medium text-gray-700 mb-2">
                  {t("userForm.ProfessionalForm.fields.income.label")}</label>
                <input
                  id="usermgmt_income"
                  type="number"
                  value={userManagementFormData.income}
                  onChange={(e) => handleUserManagementInputChange('income', e.target.value)}
                  placeholder={t("userForm.ProfessionalForm.fields.income.placeholder")}
                  disabled={userManagementCreateLoading}
                  className="form-input w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div className="form-field">
                <label htmlFor="usermgmt_height" className="form-label block text-sm font-medium text-gray-700 mb-2">    {t("userForm.ProfessionalForm.fields.height.label")}</label>
                <input
                  id="usermgmt_height"
                  type="number"
                  value={userManagementFormData.height}
                  onChange={(e) => handleUserManagementInputChange('height', e.target.value)}
                  placeholder={t("userForm.ProfessionalForm.fields.height.placeholder")}
                  min="100"
                  disabled={userManagementCreateLoading}
                  className={`form-input w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${fieldErrors.height ? 'border-red-500' : 'border-gray-300'
                    }`}
                />
                {fieldErrors.height && (
                  <p className="text-red-500 text-xs mt-1">{fieldErrors.height}</p>
                )}
                {/* <p className="text-xs text-gray-500 mt-1">{t("userForm.ProfessionalForm.fields.height.minError")}</p> */}
              </div>

              <div className="form-field">
                <label htmlFor="usermgmt_weight" className="form-label block text-sm font-medium text-gray-700 mb-2"> {t("userForm.ProfessionalForm.fields.weight.label")}</label>
                <input
                  id="usermgmt_weight"
                  type="number"
                  value={userManagementFormData.weight}
                  onChange={(e) => handleUserManagementInputChange('weight', e.target.value)}
                  placeholder={t("userForm.ProfessionalForm.fields.weight.placeholder")}
                  min="30"
                  disabled={userManagementCreateLoading}
                  className={`form-input w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${fieldErrors.weight ? 'border-red-500' : 'border-gray-300'
                    }`}
                />
                {fieldErrors.weight && (
                  <p className="text-red-500 text-xs mt-1">{fieldErrors.weight}</p>
                )}
                {/* <p className="text-xs text-gray-500 mt-1">{t("userForm.ProfessionalForm.fields.weight.minError")}</p> */}
              </div>

              <div className="form-field">
                <label htmlFor="usermgmt_motherTongue" className="form-label block text-sm font-medium text-gray-700 mb-2"> {t("userForm.professionalInfo.motherTongue.label")}</label>
                <input
                  id="usermgmt_motherTongue"
                  value={userManagementFormData.motherTongue}
                  onChange={(e) => handleUserManagementInputChange('motherTongue', e.target.value)}
                  placeholder={t("userForm.professionalInfo.motherTongue.placeholder")}
                  disabled={userManagementCreateLoading}
                  className="form-input w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                {fieldErrors.motherTongue && (
                  <p className="text-red-500 text-xs mt-1">{fieldErrors.motherTongue}</p>
                )}
              </div>

              <div className="form-field">
                <label htmlFor="usermgmt_subCaste" className="form-label block text-sm font-medium text-gray-700 mb-2"> {t("userForm.professionalInfo.subCaste.label")}</label>
                <input
                  id="usermgmt_subCaste"
                  value={userManagementFormData.subCaste}
                  onChange={(e) => handleUserManagementInputChange('subCaste', e.target.value)}
                  placeholder={t("userForm.professionalInfo.subCaste.placeholder")}
                  disabled={userManagementCreateLoading}
                  className="form-input w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div className="form-field">
                <label htmlFor="usermgmt_diet" className="form-label block text-sm font-medium text-gray-700 mb-2">
                  {t("userForm.professionalInfo.diet.label")} <span style={{ color: 'red' }}>*</span></label>
                <select
                  value={userManagementFormData.diet}
                  onChange={(e) => handleUserManagementInputChange('diet', e.target.value)}
                  disabled={userManagementCreateLoading}
                  className="form-select w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value=""> {t("userForm.professionalInfo.diet.selectPlaceholder")}</option>
                  {dietTypes.map((diet) => (
                    <option key={diet} value={diet.value}>{diet.label}</option>
                  ))}
                </select>
                {fieldErrors.diet && (
                  <p className="text-red-500 text-xs mt-1">{fieldErrors.diet}</p>
                )}
                {/* <p className="text-xs text-gray-500 mt-1">  {t("userForm.professionalInfo.diet.validOptions")} {dietTypes.join(', ')}</p> */}
              </div>

              <div className="form-field">
                <label
                  htmlFor="instagram"
                  className="form-label block text-sm font-medium text-gray-700 mb-2"
                >
                  {t("userForm.professionalInfo.instagram.label")}
                </label>

                <input
                  id="instagram"
                  value={userManagementFormData.instagram}
                  onChange={(e) =>
                    handleUserManagementInputChange("instagram", e.target.value)
                  }
                  placeholder={t("userForm.professionalInfo.instagram.placeholder")}
                  disabled={userManagementCreateLoading}
                  className="form-input w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div className="form-field">
                <label htmlFor="aboutMe" className="form-label block text-sm font-medium text-gray-700 mb-2">
                  {t("userForm.ProfessionalForm.fields.aboutMe.label")} <span style={{ color: 'red' }}>*</span>
                </label>
                <textarea
                  id="aboutMe"
                  value={userManagementFormData.aboutMe}
                  onChange={(e) => handleUserManagementInputChange("aboutMe", e.target.value)}
                  placeholder={t("userForm.ProfessionalForm.fields.aboutMe.placeholder")}
                  disabled={userManagementCreateLoading}
                  rows={3}
                  className={`form-input w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${fieldErrors.aboutMe ? "border-red-500" : "border-gray-300"
                    }`}
                  required
                />
                {fieldErrors.aboutMe && (
                  <p className="text-red-500 text-xs mt-1">{fieldErrors.aboutMe}</p>
                )}
              </div>

            </div>
          </div>


        );

      case 'astrology-info':
        return (
          <div className="form-section mb-8">
            <h3 className="section-title text-lg font-semibold mb-4 text-gray-800 border-b pb-2">   {t("userForm.astrologyInfo.title")}</h3>
            <div className="form-grid grid grid-cols-1 md:grid-cols-2 gap-4 pt-3">
              <div className="form-field">
                <label htmlFor="usermgmt_rasi" className="form-label block text-sm font-medium text-gray-700 mb-2">   {t("userForm.astrologyInfo.rasi.label")}</label>

                <input
                  id="usermgmt_rasi"
                  value={userManagementFormData.rasi}
                  placeholder={t("userForm.astrologyInfo.rasi.placeholder")}
                  onChange={(e) => handleUserManagementInputChange('rasi', e.target.value)}
                  disabled={userManagementCreateLoading}
                  className="form-select w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div className="form-field">
                <label htmlFor="usermgmt_natchathiram" className="form-label block text-sm font-medium text-gray-700 mb-2">   {t("userForm.astrologyInfo.natchathiram.label")}</label>

                <input
                  id="usermgmt_natchathiram"
                  value={userManagementFormData.natchathiram}
                  placeholder={t("userForm.astrologyInfo.natchathiram.placeholder")}
                  onChange={(e) => handleUserManagementInputChange('natchathiram', e.target.value)}
                  disabled={userManagementCreateLoading}
                  className="form-select w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div className="form-field">
                <label htmlFor="usermgmt_gothram" className="form-label block text-sm font-medium text-gray-700 mb-2">  {t("userForm.astrologyInfo.gothram.label")}</label>
                <input
                  id="usermgmt_gothram"
                  value={userManagementFormData.gothram}
                  placeholder={t("userForm.astrologyInfo.gothram.placeholder")}
                  onChange={(e) => handleUserManagementInputChange('gothram', e.target.value)}
                  disabled={userManagementCreateLoading}
                  className="form-select w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div className="form-field">
                <label htmlFor="usermgmt_laknam" className="form-label block text-sm font-medium text-gray-700 mb-2">  {t("userForm.astrologyInfo.laknam.label")}</label>

                <input
                  id="usermgmt_laknam"
                  value={userManagementFormData.laknam}
                  onChange={(e) => handleUserManagementInputChange('laknam', e.target.value)}
                  placeholder={t("userForm.astrologyInfo.laknam.placeholder")}
                  disabled={userManagementCreateLoading}
                  className="form-select w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div className="form-field">
                <label htmlFor="usermgmt_dosham" className="form-label block text-sm font-medium text-gray-700 mb-2">{t("userForm.astrologyInfo.dosham.label")}</label>
                <select
                  id="usermgmt_dosham"
                  value={userManagementFormData.dosham}
                  onChange={(e) => handleUserManagementInputChange('dosham', e.target.value)}
                  disabled={userManagementCreateLoading}
                  className="form-select w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">{t("userForm.astrologyInfo.dosham.placeholder")}</option>
                  {doshamTypes.map((dosham) => (
                    <option key={dosham} value={dosham.value}>{dosham.label}</option>
                  ))}
                </select>
                {/* <p className="text-xs text-gray-500 mt-1"> {t("userForm.astrologyInfo.dosham.validOptions")} {doshamTypes.join(', ')}</p> */}
              </div>

              <div className="form-field">
                <label htmlFor="usermgmt_kujaDosham" className="form-label block text-sm font-medium text-gray-700 mb-2">  {t("userForm.astrologyInfo.kujaDosham.label")}</label>
                <select
                  id="usermgmt_kujaDosham"
                  value={userManagementFormData.kujaDosham}
                  onChange={(e) => handleUserManagementInputChange('kujaDosham', e.target.value)}
                  disabled={userManagementCreateLoading}
                  className="form-select w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">{t("userForm.astrologyInfo.kujaDosham.placeholder")}</option>
                  {kujaDoshamTypes.map((kujaDosham) => (
                    <option key={kujaDosham} value={kujaDosham.value}>{kujaDosham.label}</option>
                  ))}
                </select>
                {/* <p className="text-xs text-gray-500 mt-1"> {t("userForm.astrologyInfo.kujaDosham.validOptions")} {kujaDoshamTypes.join(', ')}</p> */}
              </div>
            </div>
          </div>
        );

      case 'partner-preferences':
        return (
          <div className="form-section mb-8">
            <h3 className="section-title text-lg font-semibold mb-4 text-gray-800 border-b pb-2">
              {t("userForm.partnerPreferences.title")}
            </h3>

            <div className="form-grid grid grid-cols-1 md:grid-cols-2 gap-4 pt-3">

              {/* Minimum Age */}
              <div className="form-field">
                <label className="form-label block text-sm font-medium text-gray-700 mb-2">
                  {t("userForm.partnerPreferences.minAge.label")} <span style={{ color: "red" }}>*</span>
                </label>
                <input
                  type="number"
                  value={userManagementFormData.min_age}
                  onChange={(e) => handleUserManagementInputChange("min_age", e.target.value)}
                  placeholder={t("userForm.partnerPreferences.minAge.placeholder")}
                  className="form-input w-full px-3 py-2 border rounded-md"
                />

              </div>

              {/* Maximum Age */}
              <div className="form-field">
                <label className="form-label block text-sm font-medium text-gray-700 mb-2">
                  {t("userForm.partnerPreferences.maxAge.label")} <span style={{ color: "red" }}>*</span>
                </label>
                <input
                  type="number"
                  value={userManagementFormData.max_age}
                  onChange={(e) => handleUserManagementInputChange("max_age", e.target.value)}
                  placeholder={t("userForm.partnerPreferences.maxAge.placeholder")}
                  className="form-input w-full px-3 py-2 border rounded-md"
                />
              </div>

              <div className="form-field">
                <label className="form-label block text-sm font-medium text-gray-700 mb-2">
                  {t("userForm.partnerPreferences.minHeight.label")}
                </label>
                <input
                  type="number"
                  value={userManagementFormData.height_range?.min || ""}
                  onChange={(e) => handleUserManagementHeightRangeChange("min", e.target.value)}
                  placeholder={t("userForm.partnerPreferences.minHeight.placeholder")}
                  className="form-input w-full px-3 py-2 border rounded-md"
                />
                {fieldErrors.height_range_min && (
                  <p className="text-red-500 text-xs mt-1">{fieldErrors.height_range_min}</p>
                )}
              </div>

              <div className="form-field">
                <label className="form-label block text-sm font-medium text-gray-700 mb-2">
                  {t("userForm.partnerPreferences.maxHeight.label")}
                </label>
                <input
                  type="number"
                  value={userManagementFormData.height_range?.max || ""}
                  onChange={(e) => handleUserManagementHeightRangeChange("max", e.target.value)}
                  placeholder={t("userForm.partnerPreferences.maxHeight.placeholder")}
                  className="form-input w-full px-3 py-2 border rounded-md"
                />
                {fieldErrors.height_range_max && (
                  <p className="text-red-500 text-xs mt-1">{fieldErrors.height_range_max}</p>
                )}
              </div>



              <div className="form-field">
                <label className="form-label block text-sm font-medium text-gray-700 mb-2">
                  {t("userForm.partnerPreferences.religionPref.label")}
                </label>

                <Select
                  isMulti
                  options={religionListPro}
                  value={(userManagementFormData.religion_pref || []).map((value) => {
                    const match = religionLisProfile[lang].find((item) => item.value === value);
                    return match || { label: value, value };
                  })}
                  onChange={(selectedOptions) =>
                    handleUserManagementArrayChange(
                      "religion_pref",
                      selectedOptions ? selectedOptions.map((opt) => opt.value) : []
                    )
                  }
                  isDisabled={userManagementCreateLoading}
                  placeholder={t("userForm.professionalInfo.religion.placeholder")}
                  className="text-sm"
                />

                {fieldErrors.religion_pref && (
                  <p className="text-red-500 text-xs mt-1">{fieldErrors.religion_pref}</p>
                )}
              </div>

              <div className="form-field">
                <label
                  htmlFor="usermgmt_gender"
                  className="form-label block text-sm font-medium text-gray-700 mb-2"
                >
                 {t("userForm.partnerPreferences.genderPreference.label")} <span style={{ color: "red" }}>*</span>
                </label>

                 <select
                  id="usermgmt_gender"
                  value={userManagementFormData.gender_preference}
                  onChange={(e) => handleFieldChange('gender_preference', e.target.value)}
                  disabled={userManagementCreateLoading}
                  className={`form-select w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${fieldErrors.gender ? 'border-red-500' : 'border-gray-300'
                    }`}
                  required
                >
                  <option value="">{t("userForm.BasicuserForm.fields.gender.placeholder")}</option>
                  <option value="Male">{t("userForm.BasicuserForm.fields.gender.options.male")}</option>
                  <option value="Female">{t("userForm.BasicuserForm.fields.gender.options.female")}</option>
                  <option value="Other">{t("userForm.BasicuserForm.fields.gender.options.other")}</option>
                </select>	


                {/* <Select
                  id="usermgmt_gender"
                  options={[
                    { value: "Male", label: t("userForm.BasicuserForm.fields.gender.options.male") },
                    { value: "Female", label: t("userForm.BasicuserForm.fields.gender.options.female") },
                    { value: "Other", label: t("userForm.BasicuserForm.fields.gender.options.other") },
                  ]}
                  value={
                    userManagementFormData.gender_preference
                     
                  }
                  onChange={(selected) =>
                    handleFieldChange("gender_preference", selected.value )
                  }
                  isDisabled={userManagementCreateLoading}
                  placeholder={t("userForm.BasicuserForm.fields.gender.placeholder")}
                  className="text-sm"
                /> */}

                {fieldErrors.gender_preference && (
                  <p className="text-red-500 text-xs mt-1">{fieldErrors.gender_preference}</p>
                )}
              </div>



              <div className="form-field">
                <label className="form-label block text-sm font-medium text-gray-700 mb-2">
                  {t("userForm.partnerPreferences.castePref.label")}
                  <span style={{ color: "red" }}>*</span>
                </label>

                <Select
                  isMulti
                  options={casteOptionspreferences}
                  value={casteOptionspreferences.filter((item) =>
                    (userManagementFormData.caste_pref || []).includes(item.value)
                  )}
                  onChange={(selectedOptions) =>
                    handleUserManagementArrayChange(
                      "caste_pref",
                      selectedOptions ? selectedOptions.map((opt) => opt.value) : []
                    )
                  }
                  isDisabled={userManagementCreateLoading}
                  placeholder={t("userForm.BasicuserForm.fields.caste.placeholder")}
                  className="text-sm"
                />

                {fieldErrors.caste_pref && (
                  <p className="text-red-500 text-xs mt-1">{fieldErrors.caste_pref}</p>
                )}
              </div>



              {/* Sub Caste */}
              <div className="form-field">
                <label className="form-label">{t("userForm.partnerPreferences.subCastePref.label")}</label>
                <input
                  value={userManagementFormData.sub_caste?.join(", ") || ""}
                  onChange={(e) => handleUserManagementArrayChange("sub_caste", e.target.value)}
                  placeholder={t("userForm.partnerPreferences.subCastePref.placeholder")}
                  className="form-input w-full"
                />
                {fieldErrors.sub_caste && (
                  <p className="text-red-500 text-xs mt-1">{fieldErrors.sub_caste}</p>
                )}
              </div>

              {/* Education Level */}
              {/* <div className="form-field">
                <label className="form-label">{t("userForm.partnerPreferences.educationLevel.label")}</label>
                <input
                  value={userManagementFormData.education_level?.join(", ") || ""}
                  onChange={(e) => handleUserManagementArrayChange("education_level", e.target.value)}
                  placeholder={t("userForm.partnerPreferences.educationLevel.placeholder")}
                  className="form-input w-full"
                />
                {fieldErrors.education_level && (
                  <p className="text-red-500 text-xs mt-1">{fieldErrors.education_level}</p>
                )}
              </div> */}

              <div className="form-field">
                <label className="form-label">{t("userForm.partnerPreferences.educationLevel.label")}</label>
                <select
                  value={userManagementFormData.education_level}
                  onChange={(e) => handleUserManagementInputChange('education_level', e.target.value)}
                  disabled={userManagementCreateLoading}
                  className={`form-select w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${fieldErrors.education_level ? 'border-red-500' : 'border-gray-300'
                    }`}
                  required
                >
                  <option value="">{t("userForm.ProfessionalForm.fields.education.placeholder")}</option>
                  {educationOptions.map((option) => (
                    <option key={option} value={option.value}>{option.label}</option>
                  ))}
                </select>
                {fieldErrors.education_level && (
                  <p className="text-red-500 text-xs mt-1">{fieldErrors.education_level}</p>
                )}
              </div>


              <div className="form-field">
                <label className="form-label block text-sm font-medium text-gray-700 mb-2">
                  {t("userForm.BasicuserForm.fields.state.StatePreference")}
                </label>

                <Select
                  options={statesOptions}
                  value={
                    userManagementFormData.state_preference
                      ? {
                        label: userManagementFormData.state_preference,
                        value: userManagementFormData.state_preference,
                      }
                      : null
                  }
                  onChange={(selectedOption) => handleStateChange(selectedOption, "state_preference")}
                  isDisabled={userManagementCreateLoading}
                  placeholder={t("userForm.BasicuserForm.fields.state.placeholder")}
                  className="text-sm"
                />


                {fieldErrors.state_preference && (
                  <p className="text-red-500 text-xs mt-1">{fieldErrors.state_preference}</p>
                )}
              </div>


              <div className="form-field">
                <label className="form-label block text-sm font-medium text-gray-700 mb-2">
                  {t("userForm.partnerPreferences.cityPref.label")}
                  <span style={{ color: "red" }}>*</span>
                </label>

                <Select
                  isMulti
                  options={[{ label: "Any District", value: "Any District" }, ...districtsOptions]}
                  value={
                    userManagementFormData.district_preference?.map((d) => ({
                      label: d,
                      value: d,
                    })) || []
                  }
                  onChange={(selectedOptions) =>
                    handleFieldChange(
                      "district_preference",
                      selectedOptions ? selectedOptions.map((opt) => opt.value) : []
                    )
                  }
                  isDisabled={userManagementCreateLoading}
                  placeholder={t("userForm.partnerPreferences.cityPref.placeholder")}
                  className="text-sm"
                />



                {fieldErrors.district_preference && (
                  <p className="text-red-500 text-xs mt-1">{fieldErrors.district_preference}</p>
                )}
              </div>

              {/* Family Status */}
              <div className="form-field">
                <label className="form-label block text-sm font-medium text-gray-700 mb-2">
                  {t("userForm.partnerPreferences.familyStatus.label")}
                </label>

                <Select
                  isMulti
                  options={familyStatusOptions}
                  value={(userManagementFormData.family_status || []).map((value) => {
                    const option = familyStatusOptions.find(opt => opt.value === value);
                    return option ? option : { label: value, value: value };
                  })}
                  onChange={(selectedOptions) =>
                    handleUserManagementArrayChange(
                      "family_status",
                      selectedOptions ? selectedOptions.map((opt) => opt.value) : []
                    )
                  }
                  placeholder={t("userForm.partnerPreferences.familyStatus.placeholder")}
                  className="text-sm"
                />
                {fieldErrors.family_status && (
                  <p className="text-red-500 text-xs mt-1">{fieldErrors.family_status}</p>
                )}
              </div>


              {/* Family Type */}
              <div className="form-field">
                <label className="form-label block text-sm font-medium text-gray-700 mb-2">
                  {t("userForm.partnerPreferences.familyType.label")}
                </label>

                <Select
                  isMulti
                  options={familyTypeOptions}
                  value={(userManagementFormData.family_type || []).map(value => ({
                    label: familyTypeOptions.find(opt => opt.value === value)?.label || value,
                    value: value
                  }))}
                  onChange={(selectedOptions) =>
                    handleUserManagementArrayChange(
                      "family_type",
                      selectedOptions ? selectedOptions.map((opt) => opt.value) : []
                    )
                  }
                  placeholder={t("userForm.partnerPreferences.familyType.placeholder")}
                  className="text-sm"
                />
                {fieldErrors.family_type && (
                  <p className="text-red-500 text-xs mt-1">{fieldErrors.family_type}</p>
                )}
              </div>


              {/* Family Values */}
              <div className="form-field">
                <label className="form-label block text-sm font-medium text-gray-700 mb-2">
                  {t("userForm.partnerPreferences.familyValues.label")}
                </label>

                <Select
                  isMulti
                  options={familyValuesOptions}
                  value={(userManagementFormData.family_values || []).map(value => ({
                    label: familyValuesOptions.find(opt => opt.value === value)?.label || value,
                    value: value
                  }))}
                  onChange={(selectedOptions) =>
                    handleUserManagementArrayChange(
                      "family_values",
                      selectedOptions ? selectedOptions.map((opt) => opt.value) : []
                    )
                  }
                  placeholder={t("userForm.partnerPreferences.familyValues.placeholder")}
                  className="text-sm"
                />

                {fieldErrors.family_values && (
                  <p className="text-red-500 text-xs mt-1">{fieldErrors.family_values}</p>
                )}
              </div>


              {/* Mother Tongue */}
              <div className="form-field">
                <label className="form-label">{t("userForm.partnerPreferences.motherTongue.label")}</label>
                <input
                  value={userManagementFormData.mother_tongue?.join(", ") || ""}
                  onChange={(e) => handleUserManagementArrayChange("mother_tongue", e.target.value)}
                  placeholder={t("userForm.partnerPreferences.motherTongue.placeholder")}
                  className="form-input w-full"
                />
                {fieldErrors.preferencesmother_tongue && (
                  <p className="text-red-500 text-xs mt-1">{fieldErrors.preferencesmother_tongue}</p>
                )}
              </div>

              {/* About Partner */}
              <div className="form-field col-span-1 md:col-span-2">
                <label className="form-label">{t("userForm.partnerPreferences.aboutPartner.label")}</label>
                <textarea
                  value={userManagementFormData.about_partner}
                  onChange={(e) => handleUserManagementInputChange("about_partner", e.target.value)}
                  placeholder={t("userForm.partnerPreferences.aboutPartner.placeholder")}
                  className="form-textarea w-full px-3 py-2 border rounded-md"
                  rows="3"
                />
                {fieldErrors.about_partner && (
                  <p className="text-red-500 text-xs mt-1">{fieldErrors.about_partner}</p>
                )}
              </div>

            </div>
          </div>
        );

      case 'zodiac-details':
        return (
          <div className="form-section mb-8">
            <h3 className="section-title text-lg font-semibold mb-4 text-gray-800 border-b pb-2">Zodiac Details</h3>

            {/* Instruction */}
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200 mb-6">
              <p className="text-sm text-blue-800 mb-2">
                <strong>{t("zodiacInstruction.title")}</strong>
                {" "}{t("zodiacInstruction.line1")}
              </p>
              <p className="text-xs text-blue-700">
                {t("zodiacInstruction.line2")}
              </p>
            </div>

            <div className="space-y-6">
              {/* Common Zodiac Codes in Two Columns */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                  <h4 className="font-semibold text-blue-800 mb-3">Common Zodiac Codes - Column 1:</h4>
                  <div className="grid grid-cols-1 gap-2 text-sm">
                    <div className="flex justify-between items-center py-1">
                      <span className="text-gray-700">புதன்:</span>
                      <code className="bg-white px-2 py-1 rounded border border-gray-300 font-mono">pu</code>
                    </div>
                    <div className="flex justify-between items-center py-1">
                      <span className="text-gray-700">சந்திரன்:</span>
                      <code className="bg-white px-2 py-1 rounded border border-gray-300 font-mono">chan</code>
                    </div>
                    <div className="flex justify-between items-center py-1">
                      <span className="text-gray-700">சுக்ரன்:</span>
                      <code className="bg-white px-2 py-1 rounded border border-gray-300 font-mono">chk</code>
                    </div>
                    <div className="flex justify-between items-center py-1">
                      <span className="text-gray-700">சூரியன்:</span>
                      <code className="bg-white px-2 py-1 rounded border border-gray-300 font-mono">sun</code>
                    </div>
                    <div className="flex justify-between items-center py-1">
                      <span className="text-gray-700">வியாழன்:</span>
                      <code className="bg-white px-2 py-1 rounded border border-gray-300 font-mono">viy</code>
                    </div>
                    <div className="flex justify-between items-center py-1">
                      <span className="text-gray-700">குரு:</span>
                      <code className="bg-white px-2 py-1 rounded border border-gray-300 font-mono">gu</code>
                    </div>
                  </div>
                </div>

                <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                  <h4 className="font-semibold text-blue-800 mb-3">Common Zodiac Codes - Column 2:</h4>
                  <div className="grid grid-cols-1 gap-2 text-sm">
                    <div className="flex justify-between items-center py-1">
                      <span className="text-gray-700">செவ்வாய்:</span>
                      <code className="bg-white px-2 py-1 rounded border border-gray-300 font-mono">sev</code>
                    </div>
                    <div className="flex justify-between items-center py-1">
                      <span className="text-gray-700">கேது:</span>
                      <code className="bg-white px-2 py-1 rounded border border-gray-300 font-mono">kee</code>
                    </div>
                    <div className="flex justify-between items-center py-1">
                      <span className="text-gray-700">லக்னம்:</span>
                      <code className="bg-white px-2 py-1 rounded border border-gray-300 font-mono">lac</code>
                    </div>
                    <div className="flex justify-between items-center py-1">
                      <span className="text-gray-700">ராகு:</span>
                      <code className="bg-white px-2 py-1 rounded border border-gray-300 font-mono">ragu</code>
                    </div>
                    <div className="flex justify-between items-center py-1">
                      <span className="text-gray-700">சனி:</span>
                      <code className="bg-white px-2 py-1 rounded border border-gray-300 font-mono">sat</code>
                    </div>
                  </div>
                </div>
              </div>

              {/* Rasi and Navamsa Boxes */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-6">
                {/* Rasi Box */}
                <div className="space-y-4">
                  <h4 className="font-semibold text-gray-800 text-center">Rasi Chart</h4>
                  <div className="rasibox relative border-2 border-gray-800 rounded-lg p-4 bg-white">
                    {/* Row 1: Houses 1-4 */}
                    <div className="rowe flex justify-between mb-2">
                      {[1, 2, 3, 4].map(houseNum => (
                        <div key={houseNum} className="relative">
                          <input
                            type="text"
                            className="box w-24 h-10 text-center border border-gray-600 rounded text-sm px-2"
                            value={userManagementFormData.zodiac?.rasi?.[`House_${houseNum}`]?.join(' ') || ''}
                            onChange={(e) => handleZodiacUpdate('rasi', `House_${houseNum}`, e.target.value)}
                            disabled={userManagementCreateLoading}
                            placeholder={`H${houseNum}`}
                            title="Enter up to 3 zodiac codes separated by spaces (e.g., pu cha sun)"
                          />

                        </div>
                      ))}
                    </div>

                    {/* Row 2: Houses 12, empty, empty, House 5 */}
                    <div className="rowm flex justify-between mb-2">
                      <div className="relative">
                        <input
                          type="text"
                          className="box w-24 h-10 text-center border border-gray-600 rounded text-sm px-2"
                          value={userManagementFormData.zodiac?.rasi?.House_12?.join(' ') || ''}
                          onChange={(e) => handleZodiacUpdate('rasi', 'House_12', e.target.value)}
                          disabled={userManagementCreateLoading}
                          placeholder="H12"
                          title="Enter up to 3 zodiac codes separated by spaces (e.g., pu cha sun)"
                        />

                      </div>
                      <div className="flex-1 mx-1"></div>
                      <div className="flex-1 mx-1"></div>
                      <div className="relative">
                        <input
                          type="text"
                          className="box w-24 h-10 text-center border border-gray-600 rounded text-sm px-2"
                          value={userManagementFormData.zodiac?.rasi?.House_5?.join(' ') || ''}
                          onChange={(e) => handleZodiacUpdate('rasi', 'House_5', e.target.value)}
                          disabled={userManagementCreateLoading}
                          placeholder="H5"
                          title="Enter up to 3 zodiac codes separated by spaces (e.g., pu cha sun)"
                        />

                      </div>
                    </div>

                    {/* Row 3: Houses 11, empty, empty, House 6 */}
                    <div className="rowm flex justify-between mb-2">
                      <div className="relative">
                        <input
                          type="text"
                          className="box w-24 h-10 text-center border border-gray-600 rounded text-sm px-2"
                          value={userManagementFormData.zodiac?.rasi?.House_11?.join(' ') || ''}
                          onChange={(e) => handleZodiacUpdate('rasi', 'House_11', e.target.value)}
                          disabled={userManagementCreateLoading}
                          placeholder="H11"
                          title="Enter up to 3 zodiac codes separated by spaces (e.g., pu cha sun)"
                        />

                      </div>
                      <div className="flex-1 mx-1"></div>
                      <div className="flex-1 mx-1"></div>
                      <div className="relative">
                        <input
                          type="text"
                          className="box w-24 h-10 text-center border border-gray-600 rounded text-sm px-2"
                          value={userManagementFormData.zodiac?.rasi?.House_6?.join(' ') || ''}
                          onChange={(e) => handleZodiacUpdate('rasi', 'House_6', e.target.value)}
                          disabled={userManagementCreateLoading}
                          placeholder="H6"
                          title="Enter up to 3 zodiac codes separated by spaces (e.g., pu cha sun)"
                        />

                      </div>
                    </div>

                    {/* Row 4: Houses 10, 9, 8, 7 */}
                    <div className="rowe flex justify-between">
                      {[10, 9, 8, 7].map(houseNum => (
                        <div key={houseNum} className="relative">
                          <input
                            type="text"
                            className="box w-24 h-10 text-center border border-gray-600 rounded text-sm px-2"
                            value={userManagementFormData.zodiac?.rasi?.[`House_${houseNum}`]?.join(' ') || ''}
                            onChange={(e) => handleZodiacUpdate('rasi', `House_${houseNum}`, e.target.value)}
                            disabled={userManagementCreateLoading}
                            placeholder={`H${houseNum}`}
                            title="Enter up to 3 zodiac codes separated by spaces (e.g., pu cha sun)"
                          />

                        </div>
                      ))}
                    </div>

                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                      <h3 className="Rasicenterbox bg-white px-3 py-1 border border-gray-600 rounded text-sm font-semibold text-gray-800">
                        Rasi
                      </h3>
                    </div>
                  </div>
                </div>

                {/* Navamsa Box */}
                <div className="space-y-4">
                  <h4 className="font-semibold text-gray-800 text-center">Navamsa Chart</h4>
                  <div className="navamsabox relative border-2 border-gray-800 rounded-lg p-4 bg-white">
                    {/* Row 1: Houses 1-4 */}
                    <div className="rowe flex justify-between mb-2">
                      {[1, 2, 3, 4].map(houseNum => (
                        <div key={houseNum} className="relative">
                          <input
                            type="text"
                            className="box w-24 h-10 text-center border border-gray-600 rounded text-sm px-2"
                            value={userManagementFormData.zodiac?.navamsa?.[`House_${houseNum}`]?.join(' ') || ''}
                            onChange={(e) => handleZodiacUpdate('navamsa', `House_${houseNum}`, e.target.value)}
                            disabled={userManagementCreateLoading}
                            placeholder={`H${houseNum}`}
                            title="Enter up to 3 zodiac codes separated by spaces (e.g., pu cha sun)"
                          />
                        </div>
                      ))}
                    </div>

                    {/* Row 2: Houses 12, empty, empty, House 5 */}
                    <div className="rowm flex justify-between mb-2">
                      <div className="relative">
                        <input
                          type="text"
                          className="box w-24 h-10 text-center border border-gray-600 rounded text-sm px-2"
                          value={userManagementFormData.zodiac?.navamsa?.House_12?.join(' ') || ''}
                          onChange={(e) => handleZodiacUpdate('navamsa', 'House_12', e.target.value)}
                          disabled={userManagementCreateLoading}
                          placeholder="H12"
                          title="Enter up to 3 zodiac codes separated by spaces (e.g., pu cha sun)"
                        />

                      </div>
                      <div className="flex-1 mx-1"></div>
                      <div className="flex-1 mx-1"></div>
                      <div className="relative">
                        <input
                          type="text"
                          className="box w-24 h-10 text-center border border-gray-600 rounded text-sm px-2"
                          value={userManagementFormData.zodiac?.navamsa?.House_5?.join(' ') || ''}
                          onChange={(e) => handleZodiacUpdate('navamsa', 'House_5', e.target.value)}
                          disabled={userManagementCreateLoading}
                          placeholder="H5"
                          title="Enter up to 3 zodiac codes separated by spaces (e.g., pu cha sun)"
                        />
                      </div>
                    </div>

                    {/* Row 3: Houses 11, empty, empty, House 6 */}
                    <div className="rowm flex justify-between mb-2">
                      <div className="relative">
                        <input
                          type="text"
                          className="box w-24 h-10 text-center border border-gray-600 rounded text-sm px-2"
                          value={userManagementFormData.zodiac?.navamsa?.House_11?.join(' ') || ''}
                          onChange={(e) => handleZodiacUpdate('navamsa', 'House_11', e.target.value)}
                          disabled={userManagementCreateLoading}
                          placeholder="H11"
                          title="Enter up to 3 zodiac codes separated by spaces (e.g., pu cha sun)"
                        />
                      </div>
                      <div className="flex-1 mx-1"></div>
                      <div className="flex-1 mx-1"></div>
                      <div className="relative">
                        <input
                          type="text"
                          className="box w-24 h-10 text-center border border-gray-600 rounded text-sm px-2"
                          value={userManagementFormData.zodiac?.navamsa?.House_6?.join(' ') || ''}
                          onChange={(e) => handleZodiacUpdate('navamsa', 'House_6', e.target.value)}
                          disabled={userManagementCreateLoading}
                          placeholder="H6"
                          title="Enter up to 3 zodiac codes separated by spaces (e.g., pu cha sun)"
                        />
                      </div>
                    </div>

                    {/* Row 4: Houses 10, 9, 8, 7 */}
                    <div className="rowe flex justify-between">
                      {[10, 9, 8, 7].map(houseNum => (
                        <div key={houseNum} className="relative">
                          <input
                            type="text"
                            className="box w-24 h-10 text-center border border-gray-600 rounded text-sm px-2"
                            value={userManagementFormData.zodiac?.navamsa?.[`House_${houseNum}`]?.join(' ') || ''}
                            onChange={(e) => handleZodiacUpdate('navamsa', `House_${houseNum}`, e.target.value)}
                            disabled={userManagementCreateLoading}
                            placeholder={`H${houseNum}`}
                            title="Enter up to 3 zodiac codes separated by spaces (e.g., pu cha sun)"
                          />
                        </div>
                      ))}
                    </div>

                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                      <h3 className="Navamsamcenterbox bg-white px-3 py-1 border border-gray-600 rounded text-sm font-semibold text-gray-800">
                        Navamsa
                      </h3>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Current Values Display */}
            {/* <div className="mt-8 p-4 bg-gray-50 rounded-lg border">
              <h4 className="font-semibold text-gray-800 mb-3">Current Zodiac Values:</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-sm">
                <div>
                  <h5 className="font-medium text-gray-700 mb-2">Rasi Values:</h5>
                  {Object.entries(userManagementFormData.zodiac?.rasi || {}).map(([house, values]) => (
                    values.length > 0 && (
                      <div key={house} className="flex items-center gap-2 mb-1">
                        <span className="font-medium text-gray-600">{house}:</span>
                        <span className="bg-white px-2 py-1 rounded border">
                          {values.join(' ')} ({values.length}/3)
                        </span>
                      </div>
                    )
                  ))}
                  {Object.values(userManagementFormData.zodiac?.rasi || {}).every(arr => arr.length === 0) && (
                    <p className="text-gray-500 text-sm">No Rasi values entered yet</p>
                  )}
                </div>
                <div>
                  <h5 className="font-medium text-gray-700 mb-2">Navamsa Values:</h5>
                  {Object.entries(userManagementFormData.zodiac?.navamsa || {}).map(([house, values]) => (
                    values.length > 0 && (
                      <div key={house} className="flex items-center gap-2 mb-1">
                        <span className="font-medium text-gray-600">{house}:</span>
                        <span className="bg-white px-2 py-1 rounded border">
                          {values.join(' ')} ({values.length}/3)
                        </span>
                      </div>
                    )
                  ))}
                  {Object.values(userManagementFormData.zodiac?.navamsa || {}).every(arr => arr.length === 0) && (
                    <p className="text-gray-500 text-sm">No Navamsa values entered yet</p>
                  )}
                </div>
              </div>
            </div> */}
          </div>
        );

      case "subscriptions":
        const handleSubscription = async (planId) => {
          const token = localStorage.getItem('matrimony_token');
          setLoading(true);
          try {
            const payload = {
              user_id: subscriptionsUserId || id,
              plan_id: planId,
              created_by_admin: true,
              payment_type: paymentMethod,
              payment_status: "success"
            }

            const response = await postWithTokenAPI(token, '/userSubscription/create', payload)
            if (response?.statusCode == 200) {
              showToast(response.message, "success");
              navigate('/admin/user-management');
            } else {
              showToast("Something went wrong!", "error");
            }
          } catch (error) {
            console.error('Subscription error:', error);
          } finally {
            setLoading(false);
          }
        };

        const planOptions = plans.map(plan => ({
          value: plan._id,
          label: `${plan.plan_name} — ₹${plan.price} | Features: ${plan.features.length}`
        }));

        const paymentMethods = [
          { value: "upi", label: "UPI" },
          { value: "card", label: "Debit / Credit Card" },
          { value: "netbanking", label: "Net Banking" },
          { value: "cash", label: "Cash" }
        ];
        return (
          <div
            style={{
              padding: "20px",
              display: "flex",
              flexDirection: "row",
              justifyContent: 'space-evenly',
              gap: "15px",
            }}
          >

            {/* Simple Dropdown */}
            <div style={{
              marginBottom: "25px",
              width: "40%",
              display: "flex",
              flexDirection: "column",
              gap: "10px"
            }}>

              <label style={{
                fontWeight: "600",
                fontSize: "1rem",
                color: "#222",
                display: "flex",
                alignItems: "center",
                gap: "8px"
              }}>
                <FaCrown style={{ color: "#ffb300", fontSize: "18px" }} />
                Choose Subscription Plan
              </label>

              <Select
                value={planOptions.find(option => option.value === selectedPlan)}
                onChange={(selected) => setSelectedPlan(selected.value)}
                options={planOptions}
                placeholder="Select Subscription Plan "
                isSearchable={true}
                styles={{
                  control: (base, state) => ({
                    ...base,
                    padding: "4px",
                    borderRadius: "10px",
                    fontSize: "15px",
                    boxShadow: state.isFocused ? "0 0 0 2px #4a8ef0" : "none",
                    border: state.isFocused ? "2px solid #4a8ef0" : "1.8px solid #dce1e6",
                    transition: "0.3s ease",
                  }),
                  option: (base, state) => ({
                    ...base,
                    backgroundColor: state.isFocused ? "#4a8ef018" : "#fff",
                    color: "#000",
                    cursor: "pointer"
                  })
                }}
              />

              <label style={{
                fontWeight: "600",
                fontSize: "1rem",
                color: "#222",
                display: "flex",
                alignItems: "center",
                gap: "8px"
              }}>
                <FaPaypal style={{ color: "#007bff", fontSize: "18px" }} />
                Payment Method
              </label>

              <Select
                options={paymentMethods}
                value={paymentMethods.find(pm => pm.value === paymentMethod)}
                onChange={(selected) => setPaymentMethod(selected.value)}
                placeholder="Select Payment Method"
                isSearchable={true}
                styles={{
                  control: (base, state) => ({
                    ...base,
                    padding: "4px",
                    borderRadius: "10px",
                    fontSize: "15px",
                    boxShadow: state.isFocused ? "0 0 0 2px #4a8ef0" : "none",
                    border: state.isFocused ? "2px solid #4a8ef0" : "1.8px solid #dce1e6",
                    transition: "0.3s ease",
                  }),
                  option: (base, state) => ({
                    ...base,
                    backgroundColor: state.isFocused ? "#4a8ef018" : "#fff",
                    color: "#000",
                    cursor: "pointer"
                  })
                }}
              />
            </div>


            {/* Simple Plan Card */}
            {selectedPlan && (
              <div
                style={{
                  padding: "20px",
                  borderRadius: "12px",
                  background: "white",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                  border: "1px solid #e1e5e9",
                  transition: "0.3s",
                  width: "40%"
                }}
              >
                {plans.filter(p => p._id === selectedPlan).map(plan => (
                  <div key={plan._id}>
                    <div style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      marginBottom: "15px"
                    }}>
                      <h3 style={{ margin: 0, fontSize: "20px", fontWeight: "700", color: "#2c3e50" }}>
                        {plan.plan_name}
                      </h3>
                      <div style={{
                        display: "flex",
                        alignItems: "center",
                        fontSize: "18px",
                        fontWeight: "700",
                        color: "#27ae60"
                      }}>
                        <FaRupeeSign size={14} />
                        {plan.price}
                      </div>
                    </div>
                    <div style={{
                      display: "flex",
                      alignItems: "center",
                      marginBottom: "15px",
                      color: "#666",
                      fontSize: "14px"
                    }}>
                      <FaClock style={{ marginRight: "8px" }} />
                      <span>Duration: <b>{plan.duration_in_days} Days</b></span>
                    </div>
                    <div style={{ marginBottom: "20px" }}>
                      <h4 style={{
                        margin: "0 0 10px 0",
                        fontWeight: "600",
                        fontSize: "16px",
                        color: "#2c3e50",
                        display: "flex",
                        alignItems: "center"
                      }}>
                        <FaCheck style={{ marginRight: "8px", color: "#27ae60" }} />
                        Features Included
                      </h4>

                      <div style={{ paddingLeft: "10px" }}>
                        {plan.features.length > 0 ? (
                          plan.features.map((f, i) => (
                            <div key={i} style={{
                              display: "flex",
                              alignItems: "center",
                              marginBottom: "6px",
                              fontSize: "14px",
                              color: "#555"
                            }}>
                              <div style={{
                                width: "6px",
                                height: "6px",
                                borderRadius: "50%",
                                background: "#3498db",
                                marginRight: "10px"
                              }}></div>
                              {f}
                            </div>
                          ))
                        ) : (
                          <div style={{ color: "#999", fontSize: "14px" }}>
                            No features available
                          </div>
                        )}
                      </div>
                    </div>
                    <div style={{
                      display: "flex",
                      gap: "15px",
                      marginBottom: "20px",
                      padding: "15px",
                      background: "#f8f9fa",
                      borderRadius: "8px"
                    }}>
                      <div style={{ flex: 1, textAlign: "center" }}>
                        <div style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          marginBottom: "5px",
                          color: "#e74c3c"
                        }}>
                          <FaEye style={{ marginRight: "5px" }} />
                          <span style={{ fontSize: "14px", fontWeight: "600" }}>Views</span>
                        </div>
                        <div style={{ fontSize: "16px", fontWeight: "700", color: "#2c3e50" }}>
                          {plan.daily_views_limit}/day
                        </div>
                      </div>

                      <div style={{ flex: 1, textAlign: "center" }}>
                        <div style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          marginBottom: "5px",
                          color: "#3498db"
                        }}>
                          <FaComment style={{ marginRight: "5px" }} />
                          <span style={{ fontSize: "14px", fontWeight: "600" }}>Messages</span>
                        </div>
                        <div style={{ fontSize: "16px", fontWeight: "700", color: "#2c3e50" }}>
                          {plan.daily_messages_limit}/day
                        </div>
                      </div>
                    </div>

                    {
                      paymentMethod && (
                        <button
                          onClick={() => handleSubscription(plan._id)}
                          disabled={loading}
                          style={{
                            padding: "12px 20px",
                            width: "100%",
                            borderRadius: "8px",
                            background: "#111",
                            color: "white",
                            fontWeight: "600",
                            fontSize: "15px",
                            cursor: loading ? "not-allowed" : "pointer",
                            border: "none",
                            transition: "0.3s",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: "8px"
                          }}
                          onMouseOver={(e) => {
                            if (!loading) e.target.style.background = "#222"; // lighter black on hover
                          }}
                          onMouseOut={(e) => {
                            if (!loading) e.target.style.background = "#111"; // back to deep black
                          }}
                        >
                          {loading ? (
                            <>
                              <div style={{
                                width: "16px",
                                height: "16px",
                                border: "2px solid transparent",
                                borderTop: "2px solid white",
                                borderRadius: "50%",
                                animation: "spin 1s linear infinite"
                              }} />
                              Processing...
                            </>
                          ) : (
                            <>
                              <FaCrown style={{ color: "#ffd700" }} /> {/* gold icon */}
                              Upgrade Now
                            </>
                          )}
                        </button>


                      )
                    }


                  </div>
                ))}
              </div>
            )}


            {/* Add spinning animation */}
            <style>
              {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
            </style>
          </div>
        );

      default:
        return (
          <div className="form-section mb-8 cursor-pointer">
            <h3 className="section-title text-lg font-semibold mb-4 text-gray-800 border-b pb-2 cursor-pointer">Login Information</h3>
            <div className="form-grid grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="form-field">
                <label htmlFor="usermgmt_country_code" className="form-label block text-sm font-medium text-gray-700 mb-2">
                  Country Code <span style={{ color: 'red' }}>*</span>
                </label>

                <Select
                  id="usermgmt_country_code"
                  options={countryOptions}
                  value={countryOptions.find(opt => opt.value === userManagementFormData.country_code)}
                  onChange={(selected) => handleFieldChange('country_code', selected?.value || '')}
                  isDisabled={userManagementCreateLoading}
                  placeholder="Select country code"
                  className="text-sm"
                  styles={{
                    control: (base, state) => ({
                      ...base,
                      borderColor: fieldErrors.country_code ? 'red' : base.borderColor,
                      boxShadow: state.isFocused ? '0 0 0 2px #4a8ef0' : 'none',
                      borderRadius: '6px',
                      padding: '2px',
                    }),
                  }}
                />

                {fieldErrors.country_code && (
                  <p className="text-red-500 text-xs mt-1">{fieldErrors.country_code}</p>
                )}
              </div>

              <div className="form-field">
                <label htmlFor="usermgmt_mobile_number" className="form-label block text-sm font-medium text-gray-700 mb-2">Mobile Number <span style={{ color: 'red' }}>*</span></label>
                <input
                  id="usermgmt_mobile_number"
                  type="tel"
                  value={userManagementFormData.mobile_number}
                  onChange={(e) => handleMobileNumberChange(e.target.value)}
                  placeholder="Enter 10-digit mobile number"
                  disabled={userManagementCreateLoading}
                  className={`form-input w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${fieldErrors.mobile_number ? 'border-red-500' : 'border-gray-300'
                    }`}
                  maxLength="10"
                  pattern="[0-9]{10}"
                  required
                />
                {fieldErrors.mobile_number && (
                  <p className="text-red-500 text-xs mt-1">{fieldErrors.mobile_number}</p>
                )}
                {isEditMode && originalMobileNumber && userManagementFormData.mobile_number !== originalMobileNumber && (
                  <p className="text-blue-500 text-xs mt-1">
                    Mobile number will be updated from {originalMobileNumber} to {userManagementFormData.mobile_number}
                  </p>
                )}
              </div>
              <div className="form-field">
                <label htmlFor="usermgmt_email_default" className="form-label block text-sm font-medium text-gray-700 mb-2 ">
                  Email <span style={{ color: 'red' }}>*</span>
                </label>
                <input
                  id="usermgmt_email_default"
                  type="email"
                  value={userManagementFormData.email}
                  onChange={(e) => handleFieldChange('email', e.target.value)}
                  placeholder="Enter email address"
                  disabled={userManagementCreateLoading}
                  className={`form-input w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${fieldErrors.email ? 'border-red-500' : 'border-gray-300'}`}
                  required
                />
                {fieldErrors.email && (
                  <p className="text-red-500 text-xs mt-1">{fieldErrors.email}</p>
                )}
              </div>

              <div className="form-field">
                <label htmlFor="usermgmt_password_default" className="form-label block text-sm font-medium text-gray-700 mb-2 ">
                  Password {isEditMode ? '' : <span style={{ color: 'red' }}>*</span>}
                </label>
                <div className="relative">
                  <input
                    id="usermgmt_password_default"
                    type={showPassword ? 'text' : 'password'}
                    value={userManagementFormData.password}
                    onChange={(e) => handleFieldChange('password', e.target.value)}
                    placeholder={isEditMode ? "Leave empty to keep current password" : "Enter password"}
                    disabled={userManagementCreateLoading}
                    className={`form-input w-full px-3 py-2 pr-10 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${fieldErrors.password ? 'border-red-500' : 'border-gray-300'}`}
                    required={!isEditMode}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-500 hover:text-gray-700"
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
                {fieldErrors.password && (
                  <p className="text-red-500 text-xs mt-1">{fieldErrors.password}</p>
                )}
              </div>
            </div>
          </div>
        );
    }
  };

  if (loadingUserData) {
    return (
      <div className="min-h-screen bg-background">
        <Topbar />
        <div className="flex">
          <Sidebar activeSection={activeSection} setActiveSection={handleSidebarNavigation} />
          <div className="flex-1 ml-64 p-6">
            <div className="loading-container">
              <div className="loading-spinner"></div>
              <p>Loading user data...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Topbar Component */}
      <Topbar />

      <div className="admin-main-area">
        {/* Sidebar on the left */}
        <Sidebar activeSection={activeSection} setActiveSection={handleSidebarNavigation} />
        <div className="admin-content-area">
          <div className="admin-scrollable-content">
            {/* Main content area */}
            <div className="flex-1 ml-64 p-6">
              <button
                className="back-button mb-4 flex items-center gap-2 px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded-lg transition-colors"
                onClick={handleBack}
                disabled={userManagementCreateLoading}
                style={{ marginLeft: 15 }}
              >
                <ArrowLeft className="h-4 w-4" />
                Back to User Management
              </button>

              <div className="user-management-container">
                <div className="card bg-white rounded-lg shadow-sm border">
                  <div className="card-header p-6 border-b">
                    <div className="flex justify-between items-center">
                      <div>
                        <h2 className="card-title text-2xl font-bold text-gray-800">
                          {isEditMode ? t("userForm.title.edit") : t("userForm.title.create")}
                        </h2>
                        <p className="card-description text-gray-600 mt-2">
                          {isEditMode ? t("userForm.description.edit") : t("userForm.description.create")}.{" "}
                          <span dangerouslySetInnerHTML={{ __html: t("userForm.requiredFieldsNote") }} />                    </p>
                        {isEditMode && originalMobileNumber && (
                          <p
                            className="text-sm text-gray-500 mt-1"
                            dangerouslySetInnerHTML={{ __html: t("userForm.originalMobile", { mobileNumber: originalMobileNumber }) }}
                          />
                        )}
                      </div>
                      {isEditMode && (
                        <button
                          onClick={handleDeleteUser}
                          disabled={userManagementCreateLoading}
                          className="delete-button flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
                          aria-label={t("userForm.deleteButton.ariaLabel")}
                        >
                          <Trash2 className="h-4 w-4" />
                          {t("userForm.deleteButton.label")}
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Tab Navigation */}
                  <div className="border-b">
                    <div className="flex overflow-x-auto">
                      {tabs.map((tab) => (
                        <button
                          key={tab.id}
                          style={{ cursor: "pointer" }}
                          onClick={() => handleTabChange(tab.id)}
                          className={`px-4 py-3 font-medium ${activeTab === tab.id
                            ? 'border-blue-500 text-blue-600 bg-blue-50'
                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                            }`}
                        >
                          {tab.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="card-content p-6">
                    {renderFormSection()}

                    {/* Action Buttons */}
                    <div className="flex justify-end gap-4 mt-8 pt-6 border-t">
                      <button
                        onClick={handleBack}
                        disabled={userManagementCreateLoading}
                        className="px-6 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
                      >
                        Cancel
                      </button>
                      {
                        isEditMode && activeTab !== 'subscriptions' && (
                          <button
                            onClick={handleUserManagementCreate}
                            disabled={userManagementCreateLoading}
                            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-blue-400 disabled:cursor-not-allowed transition-colors flex items-center gap-2 cursor-pointer"
                          >
                            {userManagementCreateLoading ? (
                              <>
                                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                {isEditMode ? 'Updating...' : 'Creating...'}
                              </>
                            ) : (
                              isEditMode ? 'Update User' : 'Create User'
                            )}
                          </button>
                        )
                      }
                      {
                        !isEditMode && !subscriptions && !subscriptionsUserId && (
                          <div className="flex justify-end gap-3 mt-8">
                            {currentIndex > 0 && (
                              <button
                                onClick={goPrev}
                                style={{
                                  cursor: "pointer"
                                }}
                                className="px-6 py-2 bg-gray-300 text-black rounded-lg hover:bg-gray-400"
                              >
                                Save & Previous
                              </button>
                            )}

                            {currentIndex < tabs.length - 1 && (
                              <button
                                onClick={goNext}
                                style={{
                                  cursor: "pointer"
                                }}
                                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                              >
                                Save & Next
                              </button>
                            )}

                            {currentIndex === tabs.length - 1 && (
                              <button
                                onClick={handleUserManagementCreate}
                                disabled={userManagementCreateLoading}
                                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-blue-400 disabled:cursor-not-allowed flex items-center gap-2"
                              >
                                {userManagementCreateLoading ? (
                                  <>
                                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                    {isEditMode ? "Updating..." : "Creating..."}
                                  </>
                                ) : (
                                  isEditMode ? "Update User" : "Save User"
                                )}
                              </button>
                            )}

                          </div>
                        )
                      }
                      {
                        subscriptions && subscriptionsUserId && (
                          <button
                            onClick={() => { navigate('/admin/user-management'); }}
                            style={{
                              cursor: "pointer"
                            }}
                            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                          >
                            Close
                          </button>
                        )
                      }
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDeleteUser}
        userName={userManagementFormData.name}
        mobileNumber={userManagementFormData.mobile_number}
        isLoading={userManagementCreateLoading}
      />

      <style jsx>{`
        .rasibox, .navamsabox {
          background-color: white;
          border: 2px solid #1f2937 !important;
          border-radius: 0.5rem;
          padding: 1rem;
          position: relative;
        }

        .box {
          background-color: white !important;
          border: 1px solid #4b5563 !important;
          color: #1f2937 !important;
          text-align: center;
          border-radius: 0.25rem;
          padding: 0.25rem;
          font-size: 0.75rem;
        }

        .box:focus {
          outline: none;
          border-color: #3b82f6 !important;
          box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.1);
        }

        .box::placeholder {
          color: #9ca3af;
          font-size: 0.7rem;
        }

        .Rasicenterbox, .Navamsamcenterbox {
          background-color: white !important;
          color: #1f2937 !important;
          border: 1px solid #4b5563 !important;
          padding: 0.5rem 1rem;
          border-radius: 0.5rem;
          font-weight: 600;
          font-size: 0.875rem;
          z-index: 10;
        }

        .infobox {
          background-color: white !important;
          border: 1px solid #d1d5db !important;
          border-radius: 0.5rem;
          padding: 1rem;
        }

        .infobox p {
          color: #1f2937 !important;
          margin-bottom: 0.5rem;
          font-size: 0.875rem;
        }

        .infobox p:last-child {
          margin-bottom: 0;
        }

        .inforow {
          display: flex;
          gap: 1rem;
        }

        .rowe {
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .rowm {
          display: flex;
          justify-content: space-between;
          align-items: center;
          position: relative;
        }

        @media (max-width: 768px) {
          .rasibox, .navamsabox {
            padding: 0.5rem;
          }
          
          .box {
            width: 16px !important;
            height: 24px !important;
            font-size: 0.6rem;
          }
          
          .Rasicenterbox, .Navamsamcenterbox {
            padding: 0.25rem 0.5rem;
            font-size: 0.75rem;
          }
        }
      `}</style>
    </div>
  );
};

export default UserManagementCreate;